<template>
  <v-main>
    <v-container fluid>
      <!-- Profile photo preview: as large as the screen allows -->
      <v-dialog v-model="avatarPreview" width="auto" max-width="96vw">
        <v-card class="avatar-preview-card">
          <v-btn icon small class="avatar-preview-close" aria-label="Close preview" @click="avatarPreview = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <img
            v-if="doctor.image_path"
            :src="getImageUrl(doctor.image_path)"
            :alt="getFullName(doctor)"
            class="avatar-preview-img"
          />
          <div class="avatar-preview-caption">Dr. {{ getFullName(doctor) }}</div>
        </v-card>
      </v-dialog>

      <!-- Doctor Header Section -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card elevation="2" class="header-card">
            <!-- Decorative gradient banner (also carries the doctor's name, starting at the left edge) -->
            <div class="header-banner">
              <h1 class="page-title mb-1">
                Dr. {{ getFullName(doctor) }}
              </h1>
              <div class="page-subtitle d-flex align-center flex-wrap">
                <span class="role-pill mr-2">
                  <v-icon x-small class="mr-1">mdi-doctor</v-icon>
                  {{ doctor.position?.name || 'Doctor' }}
                </span>
                <span class="text-caption grey--text text--darken-1">
                  ID: <strong>{{ doctor.user_name || 'N/A' }}</strong>
                </span>
              </div>
            </div>

            <v-card-text class="pa-6 pt-0 header-card-body">
              <v-row align="end" no-gutters>
                <!-- Left Side: Doctor Info -->
                <v-col cols="12" lg="8">
                  <div class="d-flex align-end">
                    <!-- Doctor Avatar with Online Status -->
                    <div class="avatar-wrapper mr-5">
                      <v-avatar
                        size="104"
                        class="doctor-avatar"
                        :class="{ 'doctor-avatar--clickable': !!doctor.image_path }"
                        :title="doctor.image_path ? 'View photo' : ''"
                        @click="doctor.image_path && (avatarPreview = true)"
                      >
                        <v-img
                          v-if="doctor.image_path"
                          :src="getImageUrl(doctor.image_path)"
                          :alt="getFullName(doctor)"
                          class="avatar-image"
                        ></v-img>
                        <v-icon v-else color="grey lighten-1" size="52">mdi-doctor</v-icon>
                      </v-avatar>
                      <!-- Online Status Indicator -->
                      <div class="status-indicator">
                        <v-tooltip bottom>
                          <template #activator="{ on, attrs }">
                            <span
                              class="status-dot"
                              :class="doctor.online_status ? 'is-online' : 'is-offline'"
                              v-bind="attrs"
                              v-on="on"
                            ></span>
                          </template>
                          <span>{{ doctor.online_status ? 'Online' : 'Offline' }}</span>
                        </v-tooltip>
                      </div>
                    </div>

                    <!-- Doctor Details -->
                    <div class="doctor-info pb-1">
                      <!-- Specializations Display -->
                      <div class="mb-3" v-if="doctor.doctor_specialities && doctor.doctor_specialities.length > 0">
                        <v-chip
                          v-for="speciality in doctor.doctor_specialities.slice(0, 3)"
                          :key="speciality.id"
                          small
                          outlined
                          color="primary"
                          class="mr-2 mb-1 speciality-chip"
                        >
                          <v-icon left x-small>mdi-stethoscope</v-icon>
                          {{ speciality.specialization?.description }}
                        </v-chip>
                        <v-chip
                          v-if="doctor.doctor_specialities.length > 3"
                          small
                          outlined
                          color="grey"
                          class="mr-2 mb-1"
                        >
                          +{{ doctor.doctor_specialities.length - 3 }} more
                        </v-chip>
                      </div>

                      <!-- Status Chips -->
                      <div class="d-flex align-center flex-wrap mb-2">
                        <v-chip
                          :color="doctor.is_active ? 'success' : 'error'"
                          small
                          class="mr-2 mb-1 status-chip"
                        >
                          <v-icon left x-small>
                            {{ doctor.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                          </v-icon>
                          {{ doctor.is_active ? 'Active' : 'Inactive' }}
                        </v-chip>

                        <v-chip
                          :color="doctor.is_verified ? 'success' : 'warning'"
                          small
                          class="mr-2 mb-1 verification-chip"
                        >
                          <v-icon left x-small>
                            {{ doctor.is_verified ? 'mdi-account-check' : 'mdi-account-clock' }}
                          </v-icon>
                          {{ doctor.is_verified ? 'Verified' : 'Pending Verification' }}
                        </v-chip>

                        <v-chip color="orange darken-1" dark small class="mr-2 mb-1 wallet-chip">
                          <v-icon left x-small>mdi-wallet</v-icon>
                          ₹{{ formatCurrency(doctor.wallet || 0) }}
                        </v-chip>
                      </div>

                      <!-- Contact Info -->
                      <div class="contact-info d-flex flex-wrap">
                        <a
                          v-if="doctor.email"
                          :href="`mailto:${doctor.email}`"
                          class="contact-pill"
                          :title="doctor.email"
                        >
                          <v-icon x-small class="mr-1">mdi-email-outline</v-icon>
                          <span>{{ doctor.email }}</span>
                        </a>
                        <a
                          v-if="doctor.phone"
                          :href="`tel:${doctor.phone}`"
                          class="contact-pill"
                          :title="doctor.phone"
                        >
                          <v-icon x-small class="mr-1">mdi-phone-outline</v-icon>
                          <span>{{ doctor.phone }}</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Actions -->
                <v-col cols="12" lg="4">
                  <div class="d-flex align-center justify-lg-end justify-start mt-lg-0 mt-4 action-button-container flex-wrap">
                    <!-- Mark as Verified -->
                    <v-btn
                      v-if="!doctor.is_verified"
                      color="success"
                      :loading="isMarkingVerified"
                      class="action-btn"
                      rounded
                      depressed
                      @click="markDoctorVerified()"
                    >
                      <v-icon left small>mdi-shield-check</v-icon>
                      Mark as Verified
                    </v-btn>

                    <v-btn
                      color="primary"
                      outlined
                      :to="`/doctor/${$route.params.doctorId}`"
                      class="action-btn"
                      rounded
                    >
                      <v-icon left small>mdi-pencil</v-icon>
                      Edit Profile
                    </v-btn>

                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="action-btn"
                      rounded
                    >
                      <v-icon left small>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tabs Section -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="1" class="tabs-card">
            <v-tabs
              v-model="activeTab"
              background-color="grey lighten-5"
              color="primary"
              slider-color="primary"
              slider-size="3"
              show-arrows
              class="tabs-container"
            >
              <v-tab class="tab-item text-none">
                <div class="tab-content">
                  <v-icon size="18" class="tab-icon">mdi-account-circle</v-icon>
                  <span class="tab-text">Profile</span>
                </div>
              </v-tab>
              
              <v-tab class="tab-item text-none">
                <div class="tab-content">
                  <v-icon size="18" class="tab-icon">mdi-school</v-icon>
                  <span class="tab-text">Education</span>
                  <v-chip
                    v-if="doctor.education_details && doctor.education_details.length > 0"
                    x-small
                    color="success"
                    class="ml-1 tab-badge"
                  >
                    {{ doctor.education_details.length }}
                  </v-chip>
                </div>
              </v-tab>
              
              <v-tab class="tab-item text-none">
                <div class="tab-content">
                  <v-icon size="18" class="tab-icon">mdi-certificate</v-icon>
                  <span class="tab-text">Certificates</span>
                  <v-chip
                    v-if="doctor.council_registration_certificates && doctor.council_registration_certificates.length > 0"
                    x-small
                    color="info"
                    class="ml-1 tab-badge"
                  >
                    {{ doctor.council_registration_certificates.length }}
                  </v-chip>
                </div>
              </v-tab>
              
              <v-tab class="tab-item text-none">
                <div class="tab-content">
                  <v-icon size="18" class="tab-icon">mdi-bank</v-icon>
                  <span class="tab-text">Bank Details</span>
                  <v-chip
                    v-if="doctor.bank_details && doctor.bank_details.length > 0"
                    x-small
                    color="orange"
                    class="ml-1 tab-badge"
                  >
                    {{ doctor.bank_details.length }}
                  </v-chip>
                </div>
              </v-tab>
              
              <v-tab class="tab-item text-none">
                <div class="tab-content">
                  <v-icon size="18" class="tab-icon">mdi-file-document-multiple</v-icon>
                  <span class="tab-text">Documents</span>
                  <v-chip
                    v-if="doctor.user_documents && doctor.user_documents.length > 0"
                    x-small
                    color="purple"
                    class="ml-1 tab-badge"
                  >
                    {{ doctor.user_documents.length }}
                  </v-chip>
                </div>
              </v-tab>
              
              <v-tab class="tab-item text-none">
                <div class="tab-content">
                  <v-icon size="18" class="tab-icon">mdi-cash-multiple</v-icon>
                  <span class="tab-text">Transactions</span>
                </div>
              </v-tab>
              
              <v-tab class="tab-item text-none">
                <div class="tab-content">
                  <v-icon size="18" class="tab-icon">mdi-calendar-clock</v-icon>
                  <span class="tab-text">Appointments</span>
                </div>
              </v-tab>
              
              <v-tab class="tab-item text-none">
                <div class="tab-content">
                  <v-icon size="18" class="tab-icon">mdi-stethoscope</v-icon>
                  <span class="tab-text">Consultations</span>
                </div>
              </v-tab>

              <v-tab class="tab-item text-none">
                <div class="tab-content">
                  <v-icon size="18" class="tab-icon">mdi-calendar-check</v-icon>
                  <span class="tab-text">Availability</span>
                </div>
              </v-tab>

              <v-tab class="tab-item text-none">
                <div class="tab-content">
                  <v-icon size="18" class="tab-icon">mdi-account-group</v-icon>
                  <span class="tab-text">Referrals</span>
                </div>
              </v-tab>
            </v-tabs>

            <!-- Tab Content -->
            <v-tabs-items v-model="activeTab">
              
              <!-- Profile Tab -->
              <v-tab-item>
                <v-card-text class="pa-8 profile-content">
                  <v-row class="ma-0">
                    <!-- Basic Information -->
                    <v-col cols="12" lg="6" class="pa-3">
                      <v-card elevation="2" class="info-card h-100" rounded="xl">
                        <v-card-title class="pa-4 card-header">
                          <v-icon color="primary" class="mr-3" size="22">mdi-account-circle</v-icon>
                          <span class="card-title-text">Basic Information</span>
                        </v-card-title>
                        <v-card-text class="pa-2" style="min-height: 260px;">
                          <div class="info-row">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-account</v-icon>Full Name</span>
                            <span class="info-value">{{ getFullName(doctor) }}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-email-outline</v-icon>Email</span>
                            <span class="info-value">{{ doctor.email || 'N/A' }}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-phone-outline</v-icon>Phone</span>
                            <span class="info-value">{{ doctor.phone || 'N/A' }}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-cake-variant-outline</v-icon>Date of Birth</span>
                            <span class="info-value" :class="{ 'is-muted': !doctor.dob }">{{ doctor.dob ? formatDate(doctor.dob) : 'Not specified' }}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-human-male-female</v-icon>Gender</span>
                            <span class="info-value" :class="{ 'is-muted': !doctor.gender?.description }">{{ doctor.gender?.description || 'Not specified' }}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-ticket-percent-outline</v-icon>Referral Code</span>
                            <span class="info-value" :class="{ 'is-muted': !doctor.referral_code }">{{ doctor.referral_code || 'Not specified' }}</span>
                          </div>
                          <div class="info-row info-row-stacked">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-text-account</v-icon>About Doctor</span>
                            <span class="info-value" :class="{ 'is-muted': !doctor.bio }">{{ doctor.bio || 'Not specified' }}</span>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Account Status -->
                    <v-col cols="12" lg="6" class="pa-3">
                      <v-card elevation="2" class="info-card h-100" rounded="xl">
                        <v-card-title class="pa-4 card-header">
                          <v-icon color="primary" class="mr-3" size="22">mdi-shield-check</v-icon>
                          <span class="card-title-text">Account Status</span>
                        </v-card-title>
                        <v-card-text class="pa-2" style="min-height: 260px;">
                          <div class="info-row">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-power</v-icon>Status</span>
                            <v-chip :color="doctor.is_active ? 'success' : 'error'" small label>
                              <v-icon left x-small>{{ doctor.is_active ? 'mdi-check' : 'mdi-close' }}</v-icon>
                              {{ doctor.is_active ? 'Active' : 'Inactive' }}
                            </v-chip>
                          </div>
                          <div class="info-row">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-wallet-outline</v-icon>Wallet Balance</span>
                            <v-chip color="orange darken-1" dark small label>
                              <v-icon left x-small>mdi-currency-inr</v-icon>
                              {{ formatCurrency(doctor.wallet || 0) }}
                            </v-chip>
                          </div>
                          <div class="info-row">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-circle-medium</v-icon>Online Status</span>
                            <v-chip
                              :color="doctor.online_status ? 'success' : 'grey lighten-1'"
                              small label outlined
                            >
                              <span class="online-bullet" :class="{ 'is-on': doctor.online_status }"></span>
                              {{ doctor.online_status ? 'Online' : 'Offline' }}
                            </v-chip>
                          </div>
                          <div class="info-row">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-calendar-plus</v-icon>Member Since</span>
                            <span class="info-value">{{ formatDate(doctor.created_at) }}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-update</v-icon>Last Updated</span>
                            <span class="info-value">{{ formatDate(doctor.updated_at) }}</span>
                          </div>
                          <div class="info-row" v-if="doctor.registration_completed !== undefined">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-clipboard-check-outline</v-icon>Registration</span>
                            <v-chip
                              :color="doctor.registration_completed ? 'success' : 'warning'"
                              small label outlined
                            >
                              <v-icon left x-small>
                                {{ doctor.registration_completed ? 'mdi-check' : 'mdi-clock-outline' }}
                              </v-icon>
                              {{ doctor.registration_completed ? 'Completed' : 'Pending' }}
                            </v-chip>
                          </div>
                          <div class="info-row" v-if="doctor.registration_step !== undefined && doctor.registration_step !== null">
                            <span class="info-label"><v-icon x-small class="mr-2">mdi-progress-check</v-icon>Sign-up Step</span>
                            <div class="d-flex align-center">
                              <span class="text-caption grey--text mr-2">{{ registrationStepProgress }}</span>
                              <v-chip small label color="primary" outlined>
                                {{ registrationStepLabel }}
                              </v-chip>
                            </div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Specializations -->
                    <v-col cols="12" lg="6" class="pa-3">
                      <v-card elevation="2" class="info-card h-100" rounded="xl">
                        <v-card-title class="pa-4 card-header">
                          <v-icon color="primary" class="mr-3" size="22">mdi-medical-bag</v-icon>
                          <span class="card-title-text">Specializations</span>
                        </v-card-title>
                        <v-card-text class="pa-4" style="min-height: 260px;">
                          <div v-if="doctor.doctor_specialities && doctor.doctor_specialities.length > 0">
                            <div class="mb-3">
                              <v-chip
                                v-for="speciality in doctor.doctor_specialities"
                                :key="speciality.id"
                                color="primary"
                                outlined
                                small
                                class="ma-1"
                              >
                                <v-icon left x-small>mdi-stethoscope</v-icon>
                                {{ speciality.specialization?.description }}
                              </v-chip>
                            </div>
                            <v-divider class="my-3"></v-divider>
                            <div class="info-item">
                              <strong>Total Specializations:</strong>
                              <v-chip small color="primary" outlined>
                                {{ doctor.doctor_specialities.length }}
                              </v-chip>
                            </div>
                            <div class="info-item">
                              <strong>Primary Specialization:</strong>
                              <span>{{ doctor.doctor_specialities[0]?.specialization?.description || 'N/A' }}</span>
                            </div>
                            <div class="info-item">
                              <strong>Status:</strong>
                              <v-chip small color="success">
                                <v-icon left x-small>mdi-check-circle</v-icon>
                                Verified
                              </v-chip>
                            </div>
                          </div>
                          <div v-else class="d-flex flex-column align-center justify-center" style="min-height: 220px;">
                            <v-icon size="48" color="grey lighten-2" class="mb-3">mdi-medical-bag</v-icon>
                            <h4 class="grey--text">No Specializations</h4>
                            <p class="text-caption grey--text text-center mt-2">
                              No specializations have been added for this doctor yet.
                            </p>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Communication Settings -->
                    <v-col cols="12" lg="6" class="pa-3">
                      <v-card elevation="2" class="info-card h-100" rounded="xl">
                        <v-card-title class="pa-4 card-header">
                          <v-icon color="primary" class="mr-3" size="22">mdi-phone-settings</v-icon>
                          <span class="card-title-text">Communication Settings</span>
                        </v-card-title>
                        <v-card-text class="pa-4" style="min-height: 260px;">
                          <!-- Gives Consultation -->
                          <div class="info-item">
                            <div>
                              <strong>Gives Consultation:</strong>
                              <div class="text-caption grey--text">Can receive consultation bookings</div>
                            </div>
                            <div class="d-flex align-center">
                              <v-chip
                                x-small
                                :color="doctor.is_consultation ? 'success' : 'grey'"
                                dark
                                class="mr-2 font-weight-medium"
                              >{{ doctor.is_consultation ? 'Enabled' : 'Disabled' }}</v-chip>
                              <v-switch
                                v-model="doctor.is_consultation"
                                :disabled="isUpdatingConsultation"
                                :loading="isUpdatingConsultation ? 'success' : false"
                                color="success"
                                inset
                                hide-details
                                class="mt-0 pt-0"
                                @change="toggleConsultation"
                              ></v-switch>
                            </div>
                          </div>
                          <div class="info-item">
                            <strong>Chat Enabled:</strong>
                            <v-chip
                              :color="doctor.is_chat ? 'success' : 'error'"
                              small
                              class="ml-2"
                            >
                              <v-icon left x-small>mdi-chat</v-icon>
                              {{ doctor.is_chat ? 'Yes' : 'No' }}
                            </v-chip>
                          </div>
                          <div class="info-item">
                            <strong>Video Call Enabled:</strong>
                            <v-chip
                              :color="doctor.is_videocall ? 'success' : 'error'"
                              small
                              class="ml-2"
                            >
                              <v-icon left x-small>mdi-video</v-icon>
                              {{ doctor.is_videocall ? 'Yes' : 'No' }}
                            </v-chip>
                          </div>
                          <div class="info-item">
                            <strong>Audio Call Enabled:</strong>
                            <v-chip
                              :color="doctor.is_audiocall ? 'success' : 'error'"
                              small
                              class="ml-2"
                            >
                              <v-icon left x-small>mdi-phone</v-icon>
                              {{ doctor.is_audiocall ? 'Yes' : 'No' }}
                            </v-chip>
                          </div>
                          <div class="info-item">
                            <strong>Consultation Rate:</strong>
                            <span class="ml-2">
                              {{ doctor.consultation_rate_per_minute ? `₹${formatCurrency(doctor.consultation_rate_per_minute)}/min` : 'Not set' }}
                            </span>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                    
                    <!-- Pathy Verification -->
                    <v-col cols="12" lg="6" class="pa-3">
                      <v-card elevation="2" class="info-card h-100" rounded="xl">
                        <v-card-title class="pa-4 card-header">
                          <v-icon color="primary" class="mr-3" size="22">mdi-medical-bag</v-icon>
                          <span class="card-title-text">Pathy Integration</span>
                        </v-card-title>
                        <v-card-text class="pa-4">
                          <div class="info-item">
                            <strong>Pathy Type:</strong>
                            <span class="ml-2" v-if="doctor.pathy">
                              {{ doctor.pathy.description }}
                            </span>
                            <span class="ml-2 grey--text" v-else>Not specified</span>
                          </div>
                          <div v-if="!doctor.pathy || editingPathy" class="mt-3">
                            <v-select
                              v-model="selectedPathyId"
                              :items="pathyTypes"
                              item-text="description"
                              item-value="id"
                              label="Select Pathy Type"
                              :loading="isLoadingPathyTypes"
                              outlined
                              dense
                              hide-details="auto"
                              class="mb-2"
                            ></v-select>
                            <div class="d-flex justify-end gap-2">
                              <v-btn
                                v-if="editingPathy"
                                text
                                small
                                @click="editingPathy = false; selectedPathyId = null"
                              >
                                Cancel
                              </v-btn>
                              <v-btn
                                color="primary"
                                small
                                rounded
                                :disabled="!selectedPathyId"
                                :loading="isUpdatingPathy"
                                @click="updateDoctorPathy"
                              >
                                <v-icon left x-small>mdi-content-save</v-icon>
                                Save Pathy
                              </v-btn>
                            </div>
                          </div>
                          <div v-else-if="doctor.pathy" class="mt-1">
                            <v-btn
                              text
                              x-small
                              color="primary"
                              @click="editingPathy = true; selectedPathyId = doctor.pathy_id"
                            >
                              <v-icon left x-small>mdi-pencil</v-icon>
                              Edit Pathy
                            </v-btn>
                          </div>
                          <div class="info-item" v-if="doctor.pathy_id">
                            <strong>Integration Status:</strong>
                            <v-chip
                              :color="getPathyVerificationColor(doctor.is_verified_pathy)"
                              small
                              class="ml-2"
                            >
                              <v-icon left x-small>
                                {{ getPathyVerificationIcon(doctor.is_verified_pathy) }}
                              </v-icon>
                              {{ getPathyVerificationText(doctor.is_verified_pathy) }}
                            </v-chip>
                          </div>
                          
                          <!-- Pathy Verification Actions -->
                          <div class="mt-4" v-if="!doctor.is_verified_pathy && doctor.pathy_id && !pathyRejected">
                            <v-divider class="mb-3"></v-divider>
                            <div class="d-flex justify-center align-center">
                              <v-btn
                                color="success"
                                @click="approvePathyIntegration"
                                :loading="isApprovingPathy"
                                class="action-btn"
                                small
                                rounded
                              >
                                <v-icon left x-small>mdi-check</v-icon>
                                Approve Pathy
                              </v-btn>
                              <v-btn
                                color="error"
                                outlined
                                @click="rejectPathyIntegration"
                                :loading="isRejectingPathy"
                                class="action-btn ml-3"
                                small
                                rounded
                              >
                                <v-icon left x-small>mdi-close</v-icon>
                                Reject Pathy
                              </v-btn>
                            </div>
                          </div>

                          <!-- Rejected state -->
                          <div class="mt-4" v-else-if="pathyRejected">
                            <v-alert
                              type="error"
                              dense
                              outlined
                              class="mb-0"
                            >
                              <span>Pathy integration has been rejected.</span>
                            </v-alert>
                          </div>

                          <!-- Already Verified Message — only show when pathy is set AND verified -->
                          <div class="mt-4" v-else-if="doctor.pathy_id && doctor.is_verified_pathy">
                            <v-alert
                              type="success"
                              dense
                              outlined
                              class="mb-0"
                            >
                              <span>Pathy integration has been verified successfully!</span>
                            </v-alert>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- ID Documents & Consultation -->
                    <v-col cols="12" class="pa-3">
                      <v-card elevation="2" class="info-card" rounded="xl">
                        <v-card-title class="pa-4 card-header">
                          <v-icon color="primary" class="mr-3" size="22">mdi-card-account-details</v-icon>
                          <span class="card-title-text">ID Documents &amp; Consultation</span>
                        </v-card-title>

                        <v-card-text class="pa-0">

                          <!-- Document Columns -->
                          <v-row no-gutters>

                            <!-- Professional ID -->
                            <v-col cols="12" md="6" class="iddoc-doc-col pa-5">
                              <div class="iddoc-doc-header d-flex align-center mb-3">
                                <v-icon size="18" color="primary" class="mr-2">mdi-badge-account-outline</v-icon>
                                <span class="iddoc-doc-title">Professional ID</span>
                                <v-spacer></v-spacer>
                                <v-chip x-small :color="doctor.professional_id_path ? 'success' : 'error'" outlined>
                                  {{ doctor.professional_id_path ? 'Uploaded' : 'Missing' }}
                                </v-chip>
                              </div>

                              <!-- Uploaded state -->
                              <div v-if="doctor.professional_id_path" class="iddoc-file-row d-flex align-center pa-3 mb-3 rounded-lg">
                                <v-icon size="30" color="primary" class="mr-3">mdi-file-image-outline</v-icon>
                                <div class="flex-grow-1">
                                  <div class="iddoc-file-name">Document on file</div>
                                  <div class="iddoc-file-sub">Click View to open in new tab</div>
                                </div>
                                <v-tooltip bottom>
                                  <template #activator="{ on }">
                                    <v-btn icon small color="primary" v-on="on" @click="viewIdDocument(doctor.professional_id_path)">
                                      <v-icon size="18">mdi-eye-outline</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>View document</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                  <template #activator="{ on }">
                                    <v-btn icon small color="error" :loading="isDeletingProfId" v-on="on" @click="deleteIdDocument('professional_id_path')">
                                      <v-icon size="18">mdi-delete-outline</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>Remove document</span>
                                </v-tooltip>
                              </div>

                              <!-- Empty state -->
                              <div v-else class="iddoc-empty-zone d-flex flex-column align-center justify-center rounded-lg mb-3 pa-4">
                                <v-icon size="28" color="grey lighten-2">mdi-file-upload-outline</v-icon>
                                <span class="text-caption grey--text mt-1">No document uploaded yet</span>
                              </div>

                              <v-file-input
                                v-model="professionalIdFile"
                                :label="doctor.professional_id_path ? 'Replace document' : 'Upload Professional ID'"
                                accept="image/*,.pdf"
                                outlined dense hide-details="auto"
                                prepend-icon="" prepend-inner-icon="mdi-paperclip"
                              ></v-file-input>
                              <div class="d-flex justify-end mt-2" v-if="professionalIdFile">
                                <v-btn small color="primary" rounded depressed :loading="isUploadingProfId" @click="uploadIdDocument('professional_id', 'professionalIdFile', 'isUploadingProfId')">
                                  <v-icon left x-small>mdi-content-save</v-icon> Save Document
                                </v-btn>
                              </div>
                            </v-col>

                            <v-divider vertical></v-divider>

                            <!-- Government ID -->
                            <v-col cols="12" md="6" class="iddoc-doc-col pa-5">
                              <div class="iddoc-doc-header d-flex align-center mb-3">
                                <v-icon size="18" color="primary" class="mr-2">mdi-card-account-details-outline</v-icon>
                                <span class="iddoc-doc-title">Government ID</span>
                                <v-spacer></v-spacer>
                                <v-chip x-small :color="doctor.government_id_path ? 'success' : 'error'" outlined>
                                  {{ doctor.government_id_path ? 'Uploaded' : 'Missing' }}
                                </v-chip>
                              </div>

                              <!-- Uploaded state -->
                              <div v-if="doctor.government_id_path" class="iddoc-file-row d-flex align-center pa-3 mb-3 rounded-lg">
                                <v-icon size="30" color="primary" class="mr-3">mdi-file-image-outline</v-icon>
                                <div class="flex-grow-1">
                                  <div class="iddoc-file-name">Document on file</div>
                                  <div class="iddoc-file-sub">Click View to open in new tab</div>
                                </div>
                                <v-tooltip bottom>
                                  <template #activator="{ on }">
                                    <v-btn icon small color="primary" v-on="on" @click="viewIdDocument(doctor.government_id_path)">
                                      <v-icon size="18">mdi-eye-outline</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>View document</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                  <template #activator="{ on }">
                                    <v-btn icon small color="error" :loading="isDeletingGovId" v-on="on" @click="deleteIdDocument('government_id_path')">
                                      <v-icon size="18">mdi-delete-outline</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>Remove document</span>
                                </v-tooltip>
                              </div>

                              <!-- Empty state -->
                              <div v-else class="iddoc-empty-zone d-flex flex-column align-center justify-center rounded-lg mb-3 pa-4">
                                <v-icon size="28" color="grey lighten-2">mdi-file-upload-outline</v-icon>
                                <span class="text-caption grey--text mt-1">No document uploaded yet</span>
                              </div>

                              <v-file-input
                                v-model="governmentIdFile"
                                :label="doctor.government_id_path ? 'Replace document' : 'Upload Government ID'"
                                accept="image/*,.pdf"
                                outlined dense hide-details="auto"
                                prepend-icon="" prepend-inner-icon="mdi-paperclip"
                              ></v-file-input>
                              <div class="d-flex justify-end mt-2" v-if="governmentIdFile">
                                <v-btn small color="primary" rounded depressed :loading="isUploadingGovId" @click="uploadIdDocument('government_id', 'governmentIdFile', 'isUploadingGovId')">
                                  <v-icon left x-small>mdi-content-save</v-icon> Save Document
                                </v-btn>
                              </div>
                            </v-col>

                          </v-row>
                        </v-card-text>
                      </v-card>
                    </v-col>

                  </v-row>
                </v-card-text>
              </v-tab-item>

              <!-- Education Tab -->
              <v-tab-item>
                <v-card-text class="pa-4">
                  <div class="d-flex justify-between align-center mb-4">
                    <div>
                      <h3 class="mb-1">Education Details</h3>
                      <p class="text-caption grey--text mb-0">Academic qualifications and certifications</p>
                    </div>
                    <v-btn
                      color="primary"
                      small
                      :to="`/doctors/${$route.params.doctorId}/education-details/create`"
                      class="ml-4"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Education
                    </v-btn>
                  </div>

                  <v-data-table
                    :headers="educationHeaders"
                    :items="educationDetails"
                    :loading="isLoadingEducation"
                    class="elevation-1"
                  >
                    <template #[`item.qualification`]="{ item }">
                      <div class="font-weight-medium">{{ item.qualification }}</div>
                      <div class="text-caption grey--text">{{ item.specialization || 'No specialization' }}</div>
                    </template>
                    
                    <template #[`item.university`]="{ item }">
                      <div class="font-weight-medium">{{ item.university }}</div>
                    </template>
                    
                    <template #[`item.year_of_passing`]="{ item }">
                      <v-chip color="info" small outlined>
                        {{ item.year_of_passing }}
                      </v-chip>
                    </template>
                    
                    <template #[`item.status`]="{ item }">
                      <status-chip :color="getEducationStatusColor(item.status)" :label="String(getEducationStatusText(item.status))" />
                    </template>

                    <template #[`item.rejection_reason`]="{ item }">
                      <div v-if="item.status === 2 && item.rejection_reason">
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on, attrs }">
                            <span
                              v-bind="attrs"
                              v-on="on"
                              class="text-caption error--text cursor-pointer"
                            >
                              {{ item.rejection_reason.length > 30 ? item.rejection_reason.substring(0, 30) + '...' : item.rejection_reason }}
                            </span>
                          </template>
                          <span>{{ item.rejection_reason }}</span>
                        </v-tooltip>
                      </div>
                      <span v-else class="text-caption grey--text">-</span>
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <div class="d-flex align-center" style="gap:6px">
                        <v-tooltip bottom v-if="item.status === 0">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="success" class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="approveEducation(item.id)">
                              <v-icon size="18">mdi-check</v-icon>
                            </v-btn>
                          </template>
                          <span>Approve</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="item.status === 0">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="error" class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="rejectEducation(item.id)">
                              <v-icon size="18">mdi-close</v-icon>
                            </v-btn>
                          </template>
                          <span>Reject</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="item.certificate_file_path">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="blue lighten-1" dark class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="viewCertificate(item.certificate_file_path)">
                              <v-icon size="18">mdi-file-eye</v-icon>
                            </v-btn>
                          </template>
                          <span>View Certificate</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="item.status !== 1">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="primary" class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="openEditEducation(item)">
                              <v-icon size="18">mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit</span>
                        </v-tooltip>
                      </div>
                    </template>

                    <template #no-data>
                      <div class="text-center py-8">
                        <v-icon size="60" color="grey lighten-3">mdi-school</v-icon>
                        <h4 class="grey--text mt-2">No Education Details Found</h4>
                        <p class="grey--text">Add education information for this doctor.</p>
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-tab-item>

              <!-- Certificates Tab -->
              <v-tab-item>
                <v-card-text class="pa-4">
                  <div class="d-flex justify-between align-center mb-4">
                    <div>
                      <h3 class="mb-1">Council Registration Certificates</h3>
                      <p class="text-caption grey--text mb-0">Professional medical council registrations</p>
                    </div>
                    <div class="ml-4">
                      <v-btn
                        v-if="!certificates || certificates.length === 0 || certificates.every(c => c.status === 2)"
                        color="primary"
                        small
                        :to="`/doctors/${$route.params.doctorId}/council-certificates/create`"
                      >
                        <v-icon left x-small>mdi-plus</v-icon>
                        {{ certificates && certificates.some(c => c.status === 2) ? 'Add New Certificate' : 'Add Certificate' }}
                      </v-btn>
                      <v-chip v-else small color="info" outlined>
                        <v-icon left x-small>mdi-information</v-icon>
                        Certificate Added
                      </v-chip>
                    </div>
                  </div>

                  <v-data-table
                    :headers="certificateHeaders"
                    :items="certificates"
                    :loading="isLoadingCertificates"
                    class="elevation-1"
                  >
                    <template #[`item.council_name`]="{ item }">
                      <div class="font-weight-medium">{{ item.council_name }}</div>
                    </template>
                    
                    <template #[`item.registration_year`]="{ item }">
                      <v-chip color="info" small outlined>
                        {{ item.registration_year }}
                      </v-chip>
                    </template>
                    
                    <template #[`item.expiry_date`]="{ item }">
                      <div>
                        {{ formatDate(item.expiry_date) }}
                        <div class="text-caption grey--text" v-if="item.renewal_date">
                          Last renewal: {{ formatDate(item.renewal_date) }}
                        </div>
                      </div>
                    </template>
                    
                    <template #[`item.status`]="{ item }">
                      <status-chip :color="getEducationStatusColor(item.status)" :label="String(getEducationStatusText(item.status))" />
                    </template>

                    <template #[`item.rejection_reason`]="{ item }">
                      <div v-if="item.status === 2 && item.rejection_reason">
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on, attrs }">
                            <span
                              v-bind="attrs"
                              v-on="on"
                              class="text-caption error--text cursor-pointer"
                            >
                              {{ item.rejection_reason.length > 30 ? item.rejection_reason.substring(0, 30) + '...' : item.rejection_reason }}
                            </span>
                          </template>
                          <span>{{ item.rejection_reason }}</span>
                        </v-tooltip>
                      </div>
                      <span v-else class="text-caption grey--text">-</span>
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <div class="d-flex align-center" style="gap:6px">
                        <v-tooltip bottom v-if="item.status === 0">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="success" class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="approveCertificate(item)">
                              <v-icon size="18">mdi-check</v-icon>
                            </v-btn>
                          </template>
                          <span>Approve</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="item.status === 0">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="error" class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="rejectCertificate(item)">
                              <v-icon size="18">mdi-close</v-icon>
                            </v-btn>
                          </template>
                          <span>Reject</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="item.certificate_path">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="blue lighten-1" dark class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="viewCertificate(item.certificate_path)">
                              <v-icon size="18">mdi-file-pdf-box</v-icon>
                            </v-btn>
                          </template>
                          <span>View Certificate</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="item.status !== 1">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="warning" class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="openEditCertificate(item)">
                              <v-icon size="18">mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit</span>
                        </v-tooltip>
                      </div>
                    </template>

                    <template #no-data>
                      <div class="text-center py-8">
                        <v-icon size="60" color="grey lighten-3">mdi-certificate</v-icon>
                        <h4 class="grey--text mt-2">No Certificates Found</h4>
                        <p class="grey--text">Add council registration certificates for this doctor.</p>
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-tab-item>

              <!-- Bank Details Tab -->
              <v-tab-item>
                <v-card-text class="pa-4">
                  <div class="d-flex justify-between align-center mb-4">
                    <div>
                      <h3 class="mb-1">Bank Details</h3>
                      <p class="text-caption grey--text mb-0">Banking information for payments</p>
                    </div>
                    <div class="ml-4">
                      <v-btn
                        v-if="!bankDetails || bankDetails.length === 0"
                        color="primary"
                        small
                        :to="`/doctors/${$route.params.doctorId}/bank-details/create`"
                      >
                        <v-icon left x-small>mdi-plus</v-icon>
                        Add Bank Details
                      </v-btn>
                      <v-chip v-else small color="success" outlined>
                        <v-icon left x-small>mdi-check-circle</v-icon>
                        Bank Details Added
                      </v-chip>
                    </div>
                  </div>

                  <v-data-table
                    :headers="bankHeaders"
                    :items="bankDetails"
                    :loading="isLoadingBank"
                    class="elevation-1 modern-table"
                    :items-per-page="5"
                    :footer-props="{
                      'items-per-page-options': [5, 10, 25],
                      'show-current-page': true,
                      'show-first-last-page': true
                    }"
                  >
                    <template #[`item.account`]="{ item }">
                      <div class="account-cell">
                        <div class="d-flex align-center mb-1">
                          <v-icon x-small color="primary" class="mr-2">mdi-credit-card</v-icon>
                          <span class="font-weight-bold text-subtitle-2">{{ item.account_number || 'N/A' }}</span>
                        </div>
                        <div class="text-caption grey--text">{{ item.account_name || 'Account Holder Name Not Available' }}</div>
                      </div>
                    </template>
                    
                    <template #[`item.bank`]="{ item }">
                      <div class="bank-cell">
                        <div class="font-weight-bold text-subtitle-2 mb-1">{{ item.bank_name || 'Bank Name Not Available' }}</div>
                        <div class="d-flex align-center text-caption grey--text mb-1">
                          <v-icon xx-small color="grey" class="mr-1">mdi-map-marker</v-icon>
                          {{ item.branch || 'Branch Not Specified' }}
                        </div>
                        <div class="d-flex align-center text-caption grey--text mb-1">
                          <v-icon xx-small color="grey" class="mr-1">mdi-bank</v-icon>
                          IFSC: {{ item.ifsc_code || 'N/A' }}
                        </div>
                        <div class="d-flex align-center text-caption grey--text" v-if="item.micr_code">
                          <v-icon xx-small color="grey" class="mr-1">mdi-numeric</v-icon>
                          MICR: {{ item.micr_code }}
                        </div>
                      </div>
                    </template>
                    
                    <template #[`item.account_type`]="{ item }">
                      <div class="text-center">
                        <v-chip small outlined color="info">
                          <v-icon x-small left>mdi-account</v-icon>
                          {{ item.account_type && item.account_type.description ? item.account_type.description : (item.account_type || 'Savings') }}
                        </v-chip>
                      </div>
                    </template>
                    
                    <template #[`item.status`]="{ item }">
                      <status-chip :color="getEducationStatusColor(item.status)" :label="String(getEducationStatusText(item.status))" />
                    </template>

                    <template #[`item.rejection_reason`]="{ item }">
                      <div v-if="item.status === 2 && item.rejection_reason">
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on, attrs }">
                            <span
                              v-bind="attrs"
                              v-on="on"
                              class="text-caption error--text cursor-pointer"
                            >
                              {{ item.rejection_reason.length > 30 ? item.rejection_reason.substring(0, 30) + '...' : item.rejection_reason }}
                            </span>
                          </template>
                          <span>{{ item.rejection_reason }}</span>
                        </v-tooltip>
                      </div>
                      <span v-else class="text-caption grey--text">-</span>
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <div class="d-flex align-center" style="gap:6px">
                        <v-tooltip bottom v-if="item.status === 0">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="success" class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="approveBankDetails(item)">
                              <v-icon size="18">mdi-check</v-icon>
                            </v-btn>
                          </template>
                          <span>Approve</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="item.status === 0">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="error" class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="rejectBankDetails(item)">
                              <v-icon size="18">mdi-close</v-icon>
                            </v-btn>
                          </template>
                          <span>Reject</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="item.status !== 1">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="primary" class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="openEditBank(item)">
                              <v-icon size="18">mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit</span>
                        </v-tooltip>
                      </div>
                    </template>

                    <template #no-data>
                      <div class="text-center py-8">
                        <v-icon size="60" color="grey lighten-3">mdi-bank</v-icon>
                        <h4 class="grey--text mt-2">No Bank Details Found</h4>
                        <p class="grey--text">Add bank account information for this doctor.</p>
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-tab-item>

              <!-- Documents Tab -->
              <v-tab-item>
                <v-card-text class="pa-4">
                  <div class="d-flex justify-between align-center mb-4">
                    <div>
                      <h3 class="mb-1">Documents</h3>
                      <p class="text-caption grey--text mb-0">Identity and verification documents</p>
                    </div>
                    <v-btn
                      color="primary"
                      small
                      :to="`/doctors/${$route.params.doctorId}/documents/create`"
                      class="ml-4"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Document
                    </v-btn>
                  </div>

                  <v-data-table
                    :headers="documentHeaders"
                    :items="documents"
                    :loading="isLoadingDocuments"
                    class="elevation-1"
                  >
                    <template #[`item.doc_type`]="{ item }">
                      <div class="font-weight-medium">{{ (item.doc_type && item.doc_type.description) || item.doc_type_name || 'Unknown Type' }}</div>
                      <div class="text-caption grey--text" v-if="item.doc_number">
                        Doc #: {{ item.doc_number }}
                      </div>
                    </template>
                    
                    <template #[`item.file_info`]="{ item }">
                      <div v-if="item.file_path" class="d-flex align-center">
                        <v-icon left small color="primary">{{ getFileIcon(item.file_path) }}</v-icon>
                        <span class="text-caption">{{ getFileName(item.file_path) }}</span>
                      </div>
                      <span v-else class="text-caption grey--text">No file</span>
                    </template>
                    
                    <template #[`item.status`]="{ item }">
                      <status-chip :color="getEducationStatusColor(item.status)" :label="String(getEducationStatusText(item.status))" />
                    </template>

                    <template #[`item.rejection_reason`]="{ item }">
                      <div v-if="item.status === 2 && item.rejection_reason">
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on, attrs }">
                            <span
                              v-bind="attrs"
                              v-on="on"
                              class="text-caption error--text cursor-pointer"
                            >
                              {{ item.rejection_reason.length > 30 ? item.rejection_reason.substring(0, 30) + '...' : item.rejection_reason }}
                            </span>
                          </template>
                          <span>{{ item.rejection_reason }}</span>
                        </v-tooltip>
                      </div>
                      <span v-else class="text-caption grey--text">-</span>
                    </template>

                    <template #[`item.verified_info`]="{ item }">
                      <div v-if="item.verified_by">
                        <div class="text-caption">{{ getFullName(item.verified_by) }}</div>
                        <div class="text-caption grey--text">{{ formatDate(item.verified_at) }}</div>
                      </div>
                      <span v-else class="text-caption grey--text">Not verified</span>
                    </template>
                    
                    <template #[`item.created_at`]="{ item }">
                      {{ formatDate(item.created_at) }}
                    </template>
                    
                    <template #[`item.actions`]="{ item }">
                      <div class="d-flex align-center" style="gap:6px">
                        <v-tooltip bottom v-if="item.status === 0">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="success" class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="approveDocument(item)">
                              <v-icon size="18">mdi-check</v-icon>
                            </v-btn>
                          </template>
                          <span>Approve</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="item.status === 0">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="error" class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="rejectDocument(item)">
                              <v-icon size="18">mdi-close</v-icon>
                            </v-btn>
                          </template>
                          <span>Reject</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="item.file_path">
                          <template #activator="{ on, attrs }">
                            <v-btn small depressed color="blue lighten-1" dark class="px-2" style="min-width:32px;height:28px" v-bind="attrs" v-on="on" @click="viewDocument(item.file_path)">
                              <v-icon size="18">mdi-eye</v-icon>
                            </v-btn>
                          </template>
                          <span>View Document</span>
                        </v-tooltip>
                      </div>
                    </template>

                    <template #no-data>
                      <div class="text-center py-8">
                        <v-icon size="60" color="grey lighten-3">mdi-file-document</v-icon>
                        <h4 class="grey--text mt-2">No Documents Found</h4>
                        <p class="grey--text">Add documents for this doctor.</p>
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-tab-item>

              <!-- Transactions Tab -->
              <v-tab-item>
                <v-card-text class="pa-4">
                  <div class="mb-4">
                    <h3 class="mb-1">Transaction History</h3>
                    <p class="text-caption grey--text mb-0">Wallet and payment transactions</p>
                  </div>
                  
                  <v-data-table
                    :headers="transactionHeaders"
                    :items="transactions"
                    :loading="isLoadingTransactions"
                    class="elevation-1 modern-table"
                    :items-per-page="15"
                    :footer-props="{
                      'items-per-page-options': [10, 15, 25, 50],
                      'show-current-page': true,
                      'show-first-last-page': true
                    }"
                  >
                    <template #[`item.date`]="{ item }">
                      <div class="date-cell">
                        <div class="font-weight-medium text-body-2">{{ formatDate(item.created_at) }}</div>
                        <div class="text-caption grey--text">{{ formatTime(item.created_at) }}</div>
                      </div>
                    </template>
                    
                    <template #[`item.description`]="{ item }">
                      <div class="text-body-2">
                        {{ item.remarks || item.transaction_purpose?.description || '-' }}
                      </div>
                    </template>

                    <template #[`item.amount`]="{ item }">
                      <div class="text-right">
                        <v-chip
                          :color="isCreditTransaction(item.transaction_type) ? 'success' : 'error'"
                          small
                          class="font-weight-bold"
                        >
                          <v-icon x-small left>{{ isCreditTransaction(item.transaction_type) ? 'mdi-plus' : 'mdi-minus' }}</v-icon>
                          ₹{{ formatCurrency(item.amount) }}
                        </v-chip>
                      </div>
                    </template>

                    <template #[`item.transaction_type`]="{ item }">
                      <div class="text-center">
                        <v-chip
                          :color="isCreditTransaction(item.transaction_type) ? 'success' : 'warning'"
                          small
                          outlined
                        >
                          <v-icon x-small left>{{ isCreditTransaction(item.transaction_type) ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
                          {{ getTransactionTypeText(item.transaction_type) }}
                        </v-chip>
                      </div>
                    </template>

                    <template #[`item.status`]="{ item }">
                      <div class="text-center">
                        <v-chip
                          :color="getTransactionStatusColor(item.status)"
                          x-small
                          class="font-weight-medium"
                        >
                          <v-icon xx-small left>{{ getTransactionStatusIcon(item.status) }}</v-icon>
                          {{ getTransactionStatusText(item.status) }}
                        </v-chip>
                      </div>
                    </template>

                    <template #no-data>
                      <div class="text-center py-8">
                        <v-icon size="60" color="grey lighten-3">mdi-cash</v-icon>
                        <h4 class="grey--text mt-2">No Transactions Found</h4>
                        <p class="grey--text">No transaction history available for this doctor.</p>
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-tab-item>

              <!-- Appointments Tab -->
              <v-tab-item>
                <v-card-text class="pa-4">
                  <div class="mb-4">
                    <h3 class="mb-1">Appointments</h3>
                    <p class="text-caption grey--text mb-0">Scheduled appointments with patients</p>
                  </div>
                  
                  <v-data-table
                    :headers="appointmentHeaders"
                    :items="appointments"
                    :loading="isLoadingAppointments"
                    class="elevation-1 modern-table"
                    :items-per-page="10"
                    :footer-props="{
                      'items-per-page-options': [5, 10, 20],
                      'show-current-page': true,
                      'show-first-last-page': true
                    }"
                  >
                    <template #[`item.order_number`]="{ item }">
                      <div class="cell-vcenter justify-center">
                        <span class="font-weight-medium text-caption">{{ item.order_number || item.id }}</span>
                      </div>
                    </template>

                    <template #[`item.patient`]="{ item }">
                      <div class="patient-cell">
                        <div class="d-flex align-center mb-1">
                          <v-avatar size="24" class="mr-2">
                            <v-icon small color="primary">mdi-account</v-icon>
                          </v-avatar>
                          <span class="font-weight-bold text-subtitle-2">{{ getFullName(item.patient) }}</span>
                        </div>
                        <div class="text-caption grey--text d-flex align-center" v-if="item.patient?.email">
                          <v-icon xx-small color="grey" class="mr-1">mdi-email</v-icon>
                          {{ item.patient.email }}
                        </div>
                        <div class="text-caption grey--text d-flex align-center" v-if="item.patient?.phone">
                          <v-icon xx-small color="grey" class="mr-1">mdi-phone</v-icon>
                          {{ item.patient.phone }}
                        </div>
                      </div>
                    </template>

                    <template #[`item.category`]="{ item }">
                      <div class="cell-vcenter justify-center">
                        <v-chip x-small outlined color="brown" v-if="item.doctor && item.doctor.pathy">
                          <v-icon xx-small left>mdi-leaf</v-icon>
                          {{ item.doctor.pathy.description }}
                        </v-chip>
                        <span class="text-caption grey--text font-italic" v-else>Not set</span>
                      </div>
                    </template>

                    <template #[`item.appt_type`]="{ item }">
                      <div class="cell-vcenter justify-center">
                        <v-chip small :color="getAppointmentModeColor(item)" dark>
                          <v-icon x-small left>{{ getAppointmentModeIcon(item) }}</v-icon>
                          {{ getAppointmentModeText(item) }}
                        </v-chip>
                      </div>
                    </template>

                    <template #[`item.datetime`]="{ item }">
                      <div class="datetime-cell">
                        <div class="font-weight-medium text-body-2 d-flex align-center mb-1" v-if="item.date">
                          <v-icon x-small color="primary" class="mr-1">mdi-calendar</v-icon>
                          {{ formatDate(item.date) }}
                        </div>
                        <div class="text-caption grey--text d-flex align-center" v-if="item.from_time">
                          <v-icon xx-small color="grey" class="mr-1">mdi-clock</v-icon>
                          {{ item.from_time.substring(0, 5) }}<span v-if="item.to_time"> – {{ item.to_time.substring(0, 5) }}</span>
                        </div>
                        <span class="text-caption grey--text font-italic" v-if="!item.date && !item.from_time">Not specified</span>
                      </div>
                    </template>

                    <template #[`item.duration`]="{ item }">
                      <div class="cell-vcenter justify-center">
                        <v-chip x-small outlined :color="item.duration_minutes ? 'info' : 'grey'">
                          <v-icon xx-small left>mdi-timer</v-icon>
                          {{ item.duration_minutes ? `${item.duration_minutes} min` : '-' }}
                        </v-chip>
                      </div>
                    </template>

                    <template #[`item.payment`]="{ item }">
                      <div class="cell-vcenter justify-end">
                        <div
                          class="text-subtitle-2"
                          :class="item.amount_paid ? 'font-weight-bold success--text' : 'grey--text font-italic'"
                        >
                          {{ item.amount_paid ? `₹${formatCurrency(item.amount_paid)}` : '-' }}
                        </div>
                      </div>
                    </template>

                    <template #[`item.status`]="{ item }">
                      <div class="cell-vcenter justify-center" style="flex-direction: column;">
                        <v-chip
                          :color="getStatusColor(item.status)"
                          small
                          class="font-weight-medium"
                        >
                          <v-icon x-small left>{{ getStatusIcon(item.status) }}</v-icon>
                          {{ getStatusText(item.status) }}
                        </v-chip>
                        <div class="text-caption grey--text mt-1" v-if="item.reschedule_count > 0">
                          Rescheduled ×{{ item.reschedule_count }}
                        </div>
                      </div>
                    </template>

                    <template #[`item.consultation_link`]="{ item }">
                      <div class="cell-vcenter justify-center">
                        <v-chip
                          v-if="item.consultation"
                          x-small
                          color="teal"
                          dark
                          :to="`/consultations/${encryptIt(item.consultation.id)}`"
                          @click.stop
                        >
                          <v-icon left x-small>mdi-doctor</v-icon>
                          View
                        </v-chip>
                        <span class="text-caption grey--text" v-else>-</span>
                      </div>
                    </template>

                    <template #[`item.created_at`]="{ item }">
                      <div class="cell-vcenter">
                        {{ formatDate(item.created_at) }}
                      </div>
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <div class="cell-vcenter justify-center">
                        <v-tooltip bottom>
                          <template #activator="{ on, attrs }">
                            <v-btn
                              x-small
                              color="primary"
                              :to="`/appointments/${encryptIt(item.id)}`"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon x-small>mdi-eye</v-icon>
                            </v-btn>
                          </template>
                          <span>View Appointment</span>
                        </v-tooltip>
                      </div>
                    </template>

                    <template #no-data>
                      <div class="text-center py-8">
                        <v-icon size="60" color="grey lighten-3">mdi-calendar</v-icon>
                        <h4 class="grey--text mt-2">No Appointments Found</h4>
                        <p class="grey--text">No appointments scheduled for this doctor.</p>
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-tab-item>

              <!-- Consultations Tab -->
              <v-tab-item>
                <v-card-text class="pa-4">
                  <div class="mb-4">
                    <h3 class="mb-1">Consultations</h3>
                    <p class="text-caption grey--text mb-0">All consultation records for this doctor</p>
                  </div>
                  
                  <v-data-table
                    :headers="computedConsultationHeaders"
                    :items="consultations"
                    :loading="isLoadingConsultations"
                    class="elevation-1 modern-table"
                    :items-per-page="10"
                    :footer-props="{
                      'items-per-page-options': [5, 10, 20],
                      'show-current-page': true,
                      'show-first-last-page': true
                    }"
                  >
                    <template #[`item.patient`]="{ item }">
                      <div class="patient-cell">
                        <div class="d-flex align-center mb-1">
                          <v-avatar size="24" class="mr-2">
                            <v-icon small color="primary">mdi-account</v-icon>
                          </v-avatar>
                          <span class="font-weight-bold text-subtitle-2">{{ getFullName(item.patient) }}</span>
                        </div>
                        <div class="text-caption grey--text d-flex align-center" v-if="item.patient?.email">
                          <v-icon xx-small color="grey" class="mr-1">mdi-email</v-icon>
                          {{ item.patient.email }}
                        </div>
                      </div>
                    </template>

                    <template #[`item.session_type`]="{ item }">
                      <div class="cell-vcenter justify-center">
                        <v-chip small :color="item.appointment_id ? 'purple' : 'teal'" dark>
                          <v-icon x-small left>{{ item.appointment_id ? 'mdi-calendar-check' : 'mdi-lightning-bolt' }}</v-icon>
                          {{ item.appointment_id ? 'Scheduled' : 'Realtime' }}
                        </v-chip>
                      </div>
                    </template>

                    <template #[`item.category`]="{ item }">
                      <div class="cell-vcenter justify-center">
                        <v-chip x-small outlined color="brown" v-if="item.doctor && item.doctor.pathy">
                          <v-icon xx-small left>mdi-leaf</v-icon>
                          {{ item.doctor.pathy.description }}
                        </v-chip>
                        <span class="text-caption grey--text font-italic" v-else>Not set</span>
                      </div>
                    </template>

                    <template #[`item.status`]="{ item }">
                      <div class="cell-vcenter justify-center">
                        <v-chip small dark :color="getConsultationStatusColor(item.status)">
                          <v-icon x-small left>{{ getConsultationStatusIcon(item.status) }}</v-icon>
                          {{ getConsultationStatusText(item.status) }}
                        </v-chip>
                      </div>
                    </template>

                    <template #[`item.datetime`]="{ item }">
                      <div class="datetime-cell">
                        <div class="font-weight-medium text-body-2 d-flex align-center">
                          <v-icon x-small color="primary" class="mr-1">mdi-calendar</v-icon>
                          {{ formatDate(item.start_time) }}
                        </div>
                        <div class="text-caption grey--text d-flex align-center mt-1" v-if="item.start_time">
                          <v-icon xx-small color="grey" class="mr-1">mdi-clock</v-icon>
                          {{ formatTime(item.start_time) }}
                        </div>
                      </div>
                    </template>

                    <template #[`item.duration`]="{ item }">
                      <div class="cell-vcenter justify-center">
                        <v-chip x-small outlined :color="getConsultationDuration(item) !== '-' ? 'info' : 'grey'">
                          <v-icon xx-small left>mdi-timer</v-icon>
                          {{ getConsultationDuration(item) }}
                        </v-chip>
                      </div>
                    </template>

                    <template #[`item.payment`]="{ item }">
                      <div class="cell-vcenter justify-end">
                        <div
                          class="text-subtitle-2"
                          :class="getConsultationPayment(item) !== '-' ? 'font-weight-bold success--text' : 'grey--text font-italic'"
                        >
                          {{ getConsultationPayment(item) }}
                        </div>
                      </div>
                    </template>

                    <template #[`item.doctor_notes`]="{ item }">
                      <div class="cell-vcenter">
                        <div class="text-caption" v-if="item.doctor_notes">
                          {{ truncateText(item.doctor_notes, 40) }}
                        </div>
                        <div class="text-caption grey--text font-italic" v-else>No notes added</div>
                      </div>
                    </template>

                    <template #[`item.prescription_status`]="{ item }">
                      <div class="text-center d-flex flex-column align-center justify-center" style="min-height: 44px;">
                        <v-chip
                          x-small
                          :color="item.prescription ? 'success' : 'warning'"
                          :outlined="!item.prescription"
                        >
                          <v-icon xx-small left>{{ item.prescription ? 'mdi-prescription' : 'mdi-prescription' }}</v-icon>
                          {{ item.prescription ? 'Given' : 'Pending' }}
                        </v-chip>
                        <div class="text-caption grey--text mt-1" v-if="item.prescription?.diagnosis">
                          {{ truncateText(item.prescription.diagnosis, 40) }}
                        </div>
                      </div>
                    </template>

                    <template #[`item.created_at`]="{ item }">
                      <div class="cell-vcenter">
                        {{ formatDate(item.created_at) }}
                      </div>
                    </template>

                    <template #no-data>
                      <div class="text-center py-8">
                        <v-icon size="60" color="grey lighten-3">mdi-stethoscope</v-icon>
                        <h4 class="grey--text mt-2">No Consultations Found</h4>
                        <p class="grey--text">No consultation records for this doctor.</p>
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-tab-item>

              <!-- Availability Tab -->
              <v-tab-item>
                <v-card-text class="pa-5">

                  <!-- Header row -->
                  <div class="d-flex align-center justify-space-between mb-4">
                    <div class="d-flex align-center">
                      <v-icon color="primary" class="mr-2">mdi-calendar-check</v-icon>
                      <span class="text-h6 font-weight-semibold">Weekly Availability</span>
                    </div>
                    <v-chip small color="primary" outlined v-if="!isLoadingAvailability && availabilityDays.length">
                      <v-icon left x-small>mdi-clock-outline</v-icon>
                      {{ availabilityDays.filter(d => d.is_available).length }} / {{ availabilityDays.length }} days active
                    </v-chip>
                  </div>

                  <!-- Loading -->
                  <div v-if="isLoadingAvailability" class="avail-loading">
                    <health-loader message="Loading schedule..." />
                  </div>

                  <!-- Empty state -->
                  <div v-else-if="availabilityDays.length === 0" class="avail-empty">
                    <v-icon size="72" color="grey lighten-2">mdi-calendar-blank-outline</v-icon>
                    <h4 class="grey--text text--darken-1 mt-3 mb-1">No Availability Configured</h4>
                    <p class="grey--text text-caption">The doctor hasn't set up their weekly schedule yet.</p>
                  </div>

                  <!-- Days grid -->
                  <v-row v-else dense class="avail-grid">
                    <v-col
                      v-for="day in availabilityDays"
                      :key="day.id"
                      cols="12" sm="6" lg="4"
                    >
                      <div :class="['avail-day-card', day.is_available ? 'avail-day-card--active' : 'avail-day-card--inactive']">

                        <!-- Day header -->
                        <div class="avail-day-header">
                          <div class="d-flex align-center">
                            <div :class="['avail-day-dot', day.is_available ? 'avail-day-dot--green' : 'avail-day-dot--grey']"></div>
                            <span class="avail-day-name">{{ day.day }}</span>
                          </div>
                          <v-chip
                            x-small
                            :color="day.is_available ? 'success' : 'grey lighten-1'"
                            :text-color="day.is_available ? 'white' : 'grey darken-1'"
                          >
                            {{ day.is_available ? 'Available' : 'Off' }}
                          </v-chip>
                        </div>

                        <!-- Slot count summary -->
                        <div v-if="day.is_available" class="avail-slot-summary">
                          <v-icon x-small class="mr-1" color="primary">mdi-clock-time-four-outline</v-icon>
                          <span class="text-caption primary--text font-weight-medium">
                            {{ (day.doctor_available_time_slots || []).length }} slot{{ (day.doctor_available_time_slots || []).length !== 1 ? 's' : '' }}
                          </span>
                        </div>

                        <!-- Divider -->
                        <v-divider class="my-2" v-if="day.is_available"></v-divider>

                        <!-- Time slots -->
                        <div v-if="day.is_available && day.doctor_available_time_slots && day.doctor_available_time_slots.length" class="avail-slots-wrap">
                          <span
                            v-for="slot in day.doctor_available_time_slots"
                            :key="slot.id"
                            class="avail-slot-chip"
                          >
                            {{ slot.from }} – {{ slot.to }}
                          </span>
                        </div>

                        <div v-else-if="day.is_available" class="avail-no-slots">
                          <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                          No time slots added
                        </div>

                        <!-- Inactive overlay message -->
                        <div v-if="!day.is_available" class="avail-inactive-msg">
                          <v-icon x-small class="mr-1" color="grey">mdi-minus-circle-outline</v-icon>
                          <span class="grey--text text-caption">Not available this day</span>
                        </div>

                      </div>
                    </v-col>
                  </v-row>

                </v-card-text>
              </v-tab-item>

              <!-- Referrals Tab -->
              <v-tab-item>
                <v-card-text class="pa-4">
                  <div class="mb-4">
                    <h3 class="mb-1">Referrals</h3>
                    <p class="text-caption grey--text mb-0">Users who signed up using this doctor's referral code</p>
                  </div>

                  <v-data-table
                    :headers="referralHeaders"
                    :items="referrals"
                    :loading="isLoadingReferrals"
                    class="elevation-1 modern-table"
                    :items-per-page="15"
                    :footer-props="{
                      'items-per-page-options': [10, 15, 25, 50],
                      'show-current-page': true,
                      'show-first-last-page': true
                    }"
                  >
                    <template #[`item.name`]="{ item }">
                      <div class="text-body-2 font-weight-medium">
                        {{ [item.first_name, item.last_name].filter(Boolean).join(' ') || item.user_name || '-' }}
                      </div>
                    </template>

                    <template #[`item.position`]="{ item }">
                      <v-chip small outlined>
                        {{ item.position?.name || '-' }}
                      </v-chip>
                    </template>

                    <template #[`item.created_at`]="{ item }">
                      <div class="date-cell">
                        <div class="font-weight-medium text-body-2">{{ formatDate(item.created_at) }}</div>
                        <div class="text-caption grey--text">{{ formatTime(item.created_at) }}</div>
                      </div>
                    </template>

                    <template #no-data>
                      <div class="text-center py-8">
                        <v-icon size="60" color="grey lighten-3">mdi-account-group</v-icon>
                        <h4 class="grey--text mt-2">No Referrals Found</h4>
                        <p class="grey--text">No one has signed up using this doctor's referral code yet.</p>
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>

      <!-- Rejection Reason Dialog -->
      <v-dialog v-model="showRejectionDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="error white--text">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Reject {{ rejectionTypeLabel }}
          </v-card-title>
          <v-card-text class="pt-4">
            <v-alert type="warning" dense outlined class="mb-4">
              <span class="text-body-2">
                This action will reject the {{ rejectionTypeLabel.toLowerCase() }}.
                The doctor will be notified about the rejection.
              </span>
            </v-alert>

            <v-textarea
              v-model="rejectionReason"
              label="Rejection Reason *"
              placeholder="Please provide a detailed reason for rejection..."
              outlined
              rows="4"
              counter="500"
              maxlength="500"
              :rules="[v => !!v || 'Rejection reason is required', v => v.length >= 10 || 'Reason must be at least 10 characters']"
              :error-messages="rejectionReasonError"
              @input="rejectionReasonError = ''"
            ></v-textarea>

            <div class="text-caption grey--text mt-2">
              <v-icon x-small class="mr-1">mdi-information</v-icon>
              This reason will be shared with the doctor to help them understand what needs to be corrected.
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="closeRejectionDialog"
              :disabled="isSubmittingRejection"
            >
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="confirmRejection"
              :loading="isSubmittingRejection"
              :disabled="!rejectionReason || rejectionReason.length < 10"
            >
              <v-icon left small>mdi-close-circle</v-icon>
              Confirm Rejection
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Success Dialog -->
      <v-dialog v-model="successDialog" width="400">
        <v-card>
          <v-card-title class="text-center">
            <v-icon large color="success" class="mr-2">mdi-check-circle</v-icon>
            Success!
          </v-card-title>
          <v-card-text class="text-center">
            {{ successMessage }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" @click="successDialog = false">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Verification Pending Items Dialog -->
      <v-dialog v-model="verificationPendingDialog" max-width="600">
        <v-card>
          <v-card-title class="warning white--text">
            <v-icon left color="white">mdi-alert-circle</v-icon>
            Cannot Verify Doctor Yet
          </v-card-title>
          <v-card-text class="pt-4">
            <p class="mb-3">
              The following items must be approved before this doctor can be marked as verified:
            </p>
            <div
              v-for="section in verificationPendingItems"
              :key="section.key"
              class="mb-4"
            >
              <div class="font-weight-bold mb-1">
                {{ section.label }}
                <v-chip x-small color="warning" class="ml-2">
                  {{ section.pending_count }} of {{ section.total }} pending
                </v-chip>
              </div>
              <v-list dense class="py-0">
                <v-list-item
                  v-for="item in section.items"
                  :key="item.id"
                  class="px-0"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small :color="item.status === -1 ? 'error' : 'warning'">
                      {{ item.status === -1 ? 'mdi-close-circle' : 'mdi-clock-outline' }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">{{ item.description }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.rejection_reason" class="text-caption error--text">
                      Rejected: {{ item.rejection_reason }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-else class="text-caption">
                      Pending approval
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="verificationPendingDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- File Viewer -->
      <FileViewer v-model="docViewerDialog" :url="docViewerUrl" />
    </v-container>
  <!-- Edit Education Dialog -->
  <v-dialog v-model="editEducationDialog" max-width="520" persistent>
    <v-card>
      <v-card-title class="text-subtitle-1 font-weight-bold">
        <v-icon left color="primary" size="20">mdi-school</v-icon>
        Edit Education Detail
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pt-4">
        <v-text-field v-model="editEducationForm.qualification" label="Qualification *" outlined class="mb-2 prof-field" hide-details="auto" :error-messages="editEducationErrors.qualification">
          <template #prepend-inner><v-icon color="grey">mdi-school</v-icon></template>
        </v-text-field>
        <v-text-field v-model="editEducationForm.university" label="University / Institution" outlined class="mb-2 prof-field" hide-details="auto" :error-messages="editEducationErrors.university">
          <template #prepend-inner><v-icon color="grey">mdi-domain</v-icon></template>
        </v-text-field>
        <v-text-field v-model="editEducationForm.specialization" label="Specialization" outlined class="mb-2 prof-field" hide-details="auto" :error-messages="editEducationErrors.specialization">
          <template #prepend-inner><v-icon color="grey">mdi-stethoscope</v-icon></template>
        </v-text-field>
        <v-text-field v-model="editEducationForm.year_of_passing" label="Year of Passing" outlined class="mb-2 prof-field" hide-details="auto" type="number" :error-messages="editEducationErrors.year_of_passing">
          <template #prepend-inner><v-icon color="grey">mdi-calendar-check</v-icon></template>
        </v-text-field>

        <!-- Certificate Upload -->
        <v-file-input
          v-model="editEducationForm.certificate_file"
          label="Certificate/Degree Document"
          outlined
          class="mb-2 prof-field"
          hide-details="auto"
          accept=".pdf,.jpg,.jpeg,.png"
          prepend-icon=""
          :error-messages="editEducationErrors.certificate_file"
        >
          <template #prepend-inner><v-icon color="grey">mdi-file-certificate</v-icon></template>
        </v-file-input>
        <div v-if="editEducationCurrentCert" class="mb-2 d-flex align-center">
          <v-icon small color="grey" class="mr-1">mdi-paperclip</v-icon>
          <span class="caption grey--text">Current: </span>
          <a href="#" class="caption ml-1 primary--text" @click.prevent="viewCertificate(editEducationCurrentCert)">View existing certificate</a>
        </div>
        <div class="caption grey--text mb-3">Supported formats: PDF, JPG, PNG. Maximum size: 5MB</div>

      </v-card-text>
      <v-card-actions class="px-4 pb-3">
        <v-spacer></v-spacer>
        <v-btn text small @click="editEducationDialog = false">Cancel</v-btn>
        <v-btn color="primary" small depressed :loading="isSavingEducation" @click="saveEditEducation">
          <v-icon left size="16">mdi-content-save</v-icon>Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Edit Bank Details Dialog -->
  <v-dialog v-model="editBankDialog" max-width="520" persistent>
    <v-card>
      <v-card-title class="text-subtitle-1 font-weight-bold">
        <v-icon left color="primary" size="20">mdi-bank</v-icon>
        Edit Bank Details
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pt-4">
        <v-text-field v-model="editBankForm.account_name" label="Account Holder Name *" outlined class="mb-2 prof-field" hide-details="auto" :error-messages="editBankErrors.account_name">
          <template #prepend-inner><v-icon color="grey">mdi-account</v-icon></template>
        </v-text-field>
        <v-text-field v-model="editBankForm.bank_name" label="Bank Name *" outlined class="mb-2 prof-field" hide-details="auto" :error-messages="editBankErrors.bank_name">
          <template #prepend-inner><v-icon color="grey">mdi-bank</v-icon></template>
        </v-text-field>
        <v-text-field v-model="editBankForm.account_number" label="Account Number *" outlined class="mb-2 prof-field" hide-details="auto" :error-messages="editBankErrors.account_number">
          <template #prepend-inner><v-icon color="grey">mdi-credit-card-outline</v-icon></template>
        </v-text-field>
        <v-text-field v-model="editBankForm.ifsc_code" label="IFSC Code *" outlined class="mb-2 prof-field" hide-details="auto" :error-messages="editBankErrors.ifsc_code">
          <template #prepend-inner><v-icon color="grey">mdi-barcode</v-icon></template>
        </v-text-field>
        <v-text-field v-model="editBankForm.branch" label="Branch" outlined class="mb-2 prof-field" hide-details="auto" :error-messages="editBankErrors.branch">
          <template #prepend-inner><v-icon color="grey">mdi-map-marker</v-icon></template>
        </v-text-field>
        <v-text-field v-model="editBankForm.micr_code" label="MICR Code" outlined class="prof-field" hide-details="auto" :error-messages="editBankErrors.micr_code">
          <template #prepend-inner><v-icon color="grey">mdi-numeric</v-icon></template>
        </v-text-field>
      </v-card-text>
      <v-card-actions class="px-4 pb-3">
        <v-spacer></v-spacer>
        <v-btn text small @click="editBankDialog = false">Cancel</v-btn>
        <v-btn color="primary" small depressed :loading="isSavingBank" @click="saveEditBank">
          <v-icon left size="16">mdi-content-save</v-icon>Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Edit Certificate Dialog -->
  <v-dialog v-model="editCertificateDialog" max-width="580" persistent>
    <v-card>
      <v-card-title class="text-subtitle-1 font-weight-bold">
        <v-icon left color="primary" size="20">mdi-certificate</v-icon>
        Edit Council Registration Certificate
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pt-4">
        <v-text-field v-model="editCertificateForm.council_name" label="Medical Council Name *" outlined class="mb-2 prof-field" hide-details="auto" :error-messages="editCertificateErrors.council_name">
          <template #prepend-inner><v-icon color="grey">mdi-domain</v-icon></template>
        </v-text-field>
        <v-text-field v-model="editCertificateForm.registration_number" label="Registration Number" outlined class="mb-2 prof-field" hide-details="auto" :error-messages="editCertificateErrors.registration_number">
          <template #prepend-inner><v-icon color="grey">mdi-numeric</v-icon></template>
        </v-text-field>
        <v-text-field v-model="editCertificateForm.registration_year" label="Registration Year" outlined class="mb-2 prof-field" hide-details="auto" type="number" :error-messages="editCertificateErrors.registration_year">
          <template #prepend-inner><v-icon color="grey">mdi-calendar</v-icon></template>
        </v-text-field>
        <v-menu v-model="editCertExpiryMenu" :close-on-content-click="false" offset-y min-width="auto">
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="editCertificateForm.expiry_date"
              label="Expiry Date"
              outlined
              class="mb-2 prof-field"
              hide-details="auto"
              readonly
              clearable
              :error-messages="editCertificateErrors.expiry_date"
              v-bind="attrs"
              v-on="on"
            >
              <template #prepend-inner><v-icon color="grey">mdi-calendar-clock</v-icon></template>
            </v-text-field>
          </template>
          <v-date-picker v-model="editCertificateForm.expiry_date" no-title @input="editCertExpiryMenu = false"></v-date-picker>
        </v-menu>
        <v-menu v-model="editCertRenewalMenu" :close-on-content-click="false" offset-y min-width="auto">
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="editCertificateForm.renewal_date"
              label="Last Renewal Date"
              outlined
              class="mb-2 prof-field"
              hide-details="auto"
              readonly
              clearable
              :error-messages="editCertificateErrors.renewal_date"
              v-bind="attrs"
              v-on="on"
            >
              <template #prepend-inner><v-icon color="grey">mdi-calendar-refresh</v-icon></template>
            </v-text-field>
          </template>
          <v-date-picker v-model="editCertificateForm.renewal_date" no-title @input="editCertRenewalMenu = false"></v-date-picker>
        </v-menu>
        <v-file-input
          v-model="editCertificateForm.certificate_path"
          label="Upload Certificate Document"
          outlined
          class="mb-1 prof-field"
          hide-details="auto"
          accept=".pdf,.jpg,.jpeg,.png"
          prepend-icon=""
          :error-messages="editCertificateErrors.certificate_path"
        >
          <template #prepend-inner><v-icon color="grey">mdi-file-pdf-box</v-icon></template>
        </v-file-input>
        <div v-if="editCertificateCurrentFile" class="mb-2 d-flex align-center">
          <v-icon small color="grey" class="mr-1">mdi-paperclip</v-icon>
          <span class="caption grey--text">Current: </span>
          <a href="#" class="caption ml-1 primary--text" @click.prevent="viewCertificate(editCertificateCurrentFile)">View existing certificate</a>
        </div>
        <div class="caption grey--text mb-1">Supported formats: PDF, JPG, PNG. Maximum size: 5MB</div>
      </v-card-text>
      <v-card-actions class="px-4 pb-3">
        <v-spacer></v-spacer>
        <v-btn text small @click="editCertificateDialog = false">Cancel</v-btn>
        <v-btn color="primary" small depressed :loading="isSavingCertificate" @click="saveEditCertificate">
          <v-icon left size="16">mdi-content-save</v-icon>Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  </v-main>
</template>

<script>
import axios from "@/axios";
import { mapGetters } from "vuex";
import { FileViewer } from "@/components";

export default {
  name: "DoctorDetailPage",
  components: { FileViewer },
  data() {
    return {
      avatarPreview: false,
      activeTab: 0,
      doctor: {},
      educationDetails: [],
      certificates: [],
      bankDetails: [],
      documents: [],
      transactions: [],
      appointments: [],
      consultations: [],
      referrals: [],
      isLoading: false,
      isLoadingEducation: false,
      isLoadingCertificates: false,
      isLoadingBank: false,
      isLoadingDocuments: false,
      isLoadingTransactions: false,
      isLoadingAppointments: false,
      isLoadingConsultations: false,
      isLoadingAvailability: false,
      isLoadingReferrals: false,
      availabilityDays: [],
      isApproving: false,
      isRejecting: false,
      isApprovingPathy: false,
      isMarkingVerified: false,
      verificationPendingDialog: false,
      verificationPendingItems: [],
      isRejectingPathy: false,
      pathyRejected: false,
      editingPathy: false,
      pathyTypes: [],
      selectedPathyId: null,
      isLoadingPathyTypes: false,
      isUpdatingPathy: false,

      // ID Documents & Consultation
      isUpdatingConsultation: false,
      professionalIdFile: null,
      governmentIdFile: null,
      isUploadingProfId: false,
      isUploadingGovId: false,
      isDeletingProfId: false,
      isDeletingGovId: false,

      successDialog: false,
      successMessage: "",

      // Document Viewer
      docViewerDialog: false,
      docViewerUrl: '',

      // Rejection Dialog
      showRejectionDialog: false,
      rejectionReason: "",
      rejectionReasonError: "",
      rejectionItem: null,
      rejectionType: null, // 'education', 'certificate', 'bankDetails', 'document'
      isSubmittingRejection: false,

      // Edit Education
      editEducationDialog: false,
      editEducationId: null,
      editEducationForm: { qualification: "", university: "", specialization: "", year_of_passing: "", is_active: true, certificate_file: null },
      editEducationCurrentCert: null,
      editEducationErrors: {},
      isSavingEducation: false,

      // Edit Bank
      editBankDialog: false,
      editBankId: null,
      editBankForm: { account_name: "", bank_name: "", account_number: "", ifsc_code: "", branch: "", micr_code: "" },
      editBankErrors: {},
      isSavingBank: false,

      // Edit Certificate
      editCertificateDialog: false,
      editCertificateId: null,
      editCertificateCurrentFile: null,
      editCertificateForm: { council_name: "", registration_number: "", registration_year: "", expiry_date: "", renewal_date: "", certificate_path: null },
      editCertificateErrors: {},
      isSavingCertificate: false,
      editCertExpiryMenu: false,
      editCertRenewalMenu: false,
      
      // Table Headers
      educationHeaders: [
        { text: "Qualification & Specialization", value: "qualification", width: "25%", sortable: true },
        { text: "University/Institution", value: "university", width: "20%", sortable: true },
        { text: "Year of Passing", value: "year_of_passing", width: "10%", sortable: true, align: "center" },
        { text: "Verification Status", value: "status", width: "12%", sortable: false, align: "center" },
        { text: "Rejection Reason", value: "rejection_reason", width: "18%", sortable: false },
        { text: "Actions", value: "actions", width: "15%", sortable: false, align: "center" },
      ],
      
      certificateHeaders: [
        { text: "Council Information", value: "council_name", width: "22%", sortable: true },
        { text: "Registration Year", value: "registration_year", width: "10%", sortable: true, align: "center" },
        { text: "Validity Period", value: "expiry_date", width: "12%", sortable: true },
        { text: "Status", value: "status", width: "10%", sortable: false, align: "center" },
        { text: "Rejection Reason", value: "rejection_reason", width: "18%", sortable: false },
        { text: "Actions", value: "actions", width: "14%", sortable: false, align: "center" },
      ],
      
      bankHeaders: [
        { text: "Account Information", value: "account", width: "22%", sortable: true },
        { text: "Bank & Branch Details", value: "bank", width: "25%", sortable: true },
        { text: "Account Type", value: "account_type", width: "10%", sortable: true },
        { text: "Verification Status", value: "status", width: "12%", sortable: false, align: "center" },
        { text: "Rejection Reason", value: "rejection_reason", width: "18%", sortable: false },
        { text: "Actions", value: "actions", width: "13%", sortable: false, align: "center" },
      ],
      
      documentHeaders: [
        { text: "Document Information", value: "doc_type", width: "18%", sortable: true },
        { text: "File Details", value: "file_info", width: "12%", sortable: false },
        { text: "Upload Date", value: "created_at", width: "10%", sortable: true },
        { text: "Verification Status", value: "status", width: "10%", sortable: false, align: "center" },
        { text: "Rejection Reason", value: "rejection_reason", width: "18%", sortable: false },
        { text: "Verified By", value: "verified_info", width: "16%", sortable: false },
        { text: "Actions", value: "actions", width: "16%", sortable: false, align: "center" },
      ],
      
      transactionHeaders: [
        { text: "Transaction Date", value: "date", width: "15%", sortable: true },
        { text: "Amount", value: "amount", width: "15%", sortable: true, align: "end" },
        { text: "Transaction Type", value: "transaction_type", width: "15%", sortable: false, align: "center" },
        { text: "Status", value: "status", width: "10%", sortable: false, align: "center" },
        { text: "Description", value: "description", width: "30%", sortable: false },
      ],
      
      appointmentHeaders: [
        { text: "ID", value: "order_number", width: "6%", sortable: false, align: "center" },
        { text: "Patient Information", value: "patient", width: "17%", sortable: true },
        { text: "Category", value: "category", width: "8%", sortable: false, align: "center" },
        { text: "Type", value: "appt_type", width: "8%", sortable: false, align: "center" },
        { text: "Time", value: "datetime", width: "13%", sortable: true },
        { text: "Duration", value: "duration", width: "7%", sortable: false, align: "center" },
        { text: "Payment", value: "payment", width: "8%", sortable: true, align: "end" },
        { text: "Status", value: "status", width: "10%", sortable: false, align: "center" },
        { text: "Consultation", value: "consultation_link", width: "9%", sortable: false, align: "center" },
        { text: "Created", value: "created_at", width: "8%", sortable: true },
        { text: "Actions", value: "actions", width: "6%", sortable: false, align: "center" },
      ],
      
      consultationHeaders: [
        { text: "Patient Information", value: "patient", width: "16%", sortable: true },
        { text: "Type", value: "session_type", width: "9%", sortable: false, align: "center" },
        { text: "Category", value: "category", width: "9%", sortable: false, align: "center" },
        { text: "Status", value: "status", width: "9%", sortable: false, align: "center" },
        { text: "Date", value: "datetime", width: "11%", sortable: true },
        { text: "Duration", value: "duration", width: "7%", sortable: false, align: "center" },
        { text: "Payment", value: "payment", width: "9%", sortable: false, align: "end" },
        { text: "Doctor Notes", value: "doctor_notes", width: "12%", sortable: false },
        { text: "Prescription", value: "prescription_status", width: "13%", sortable: false, align: "center" },
        { text: "Created", value: "created_at", width: "9%", sortable: true },
      ],

      referralHeaders: [
        { text: "Name", value: "name", width: "30%", sortable: false },
        { text: "Phone", value: "phone", width: "18%", sortable: true },
        { text: "Email", value: "email", width: "25%", sortable: true },
        { text: "Position", value: "position", width: "12%", sortable: false, align: "center" },
        { text: "Joined On", value: "created_at", width: "15%", sortable: true },
      ],
    };
  },

  computed: {
    rejectionTypeLabel() {
      const labels = {
        education: 'Education Details',
        certificate: 'Certificate',
        bankDetails: 'Bank Details',
        document: 'Document'
      };
      return labels[this.rejectionType] || 'Item';
    },

    registrationStepLabel() {
      const step = Number(this.doctor.registration_step ?? 0);
      const labels = {
        0: 'Account Created',
        1: 'Education',
        2: 'Council Registration',
        3: 'Specialization',
        4: 'Documents',
        5: 'Bank Details',
      };
      return labels[step] || 'Unknown';
    },

    registrationStepProgress() {
      const step = Number(this.doctor.registration_step ?? 0);
      // 5 progressive steps after account creation (1..5)
      return `${step}/5`;
    },

    ...mapGetters("auth", ["isSuperAdmin", "isAdmin"]),
    isAdminOrSuperAdmin() {
      return this.isSuperAdmin || this.isAdmin;
    },
    computedConsultationHeaders() {
      return this.consultationHeaders.filter(h => h.value !== 'doctor_notes' || this.isAdminOrSuperAdmin);
    },
  },

  watch: {
    activeTab(newVal) {
      this.loadTabData(newVal);
    }
  },
  
  mounted() {
    this.getDoctorData();
    this.loadTabData(0); // Load first tab data
    this.loadPathyTypes();
  },
  
  methods: {
    async getDoctorData() {
      try {
        this.isLoading = true;
        const response = await axios.get(`/users/${this.$route.params.doctorId}`);
        this.doctor = response.data.data;
      } catch (error) {
        console.error("Error fetching doctor data:", error);
        this.$toast.error("Failed to load doctor information");
        if (error.response?.status === 401) {
          window.location.href = "/auth/login";
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    async loadTabData(tabIndex) {
      switch (tabIndex) {
        case 1: // Education
          this.loadEducationDetails();
          break;
        case 2: // Certificates
          this.loadCertificates();
          break;
        case 3: // Bank Details
          this.loadBankDetails();
          break;
        case 4: // Documents
          this.loadDocuments();
          break;
        case 5: // Transactions
          this.loadTransactions();
          break;
        case 6: // Appointments
          this.loadAppointments();
          break;
        case 7: // Consultations
          this.loadConsultations();
          break;
        case 8: // Availability
          this.loadAvailability();
          break;
        case 9: // Referrals
          this.loadReferrals();
          break;
      }
    },
    
    async loadEducationDetails() {
      try {
        this.isLoadingEducation = true;
        const response = await axios.get(`/education_details?user_id=${this.decryptIt(this.$route.params.doctorId)}`);
        this.educationDetails = response.data.data || [];
      } catch (error) {
        console.error("Error loading education details:", error);
      } finally {
        this.isLoadingEducation = false;
      }
    },
    
    async loadCertificates() {
      try {
        this.isLoadingCertificates = true;
        const response = await axios.get(`/council_registration_certificates?user_id=${this.decryptIt(this.$route.params.doctorId)}`);
        this.certificates = response.data.data || [];
      } catch (error) {
        console.error("Error loading certificates:", error);
      } finally {
        this.isLoadingCertificates = false;
      }
    },
    
    async loadBankDetails() {
      try {
        this.isLoadingBank = true;
        const response = await axios.get(`/bank_details?user_id=${this.decryptIt(this.$route.params.doctorId)}`);
        this.bankDetails = response.data.data || [];
      } catch (error) {
        console.error("Error loading bank details:", error);
      } finally {
        this.isLoadingBank = false;
      }
    },
    
    async loadDocuments() {
      try {
        this.isLoadingDocuments = true;
        const response = await axios.get(`/user_documents?user_id=${this.decryptIt(this.$route.params.doctorId)}`);
        this.documents = response.data.data || [];
      } catch (error) {
        console.error("Error loading documents:", error);
      } finally {
        this.isLoadingDocuments = false;
      }
    },
    
    async loadTransactions() {
      try {
        this.isLoadingTransactions = true;
        const response = await axios.get(`/transactions?user_id=${this.decryptIt(this.$route.params.doctorId)}`);
        this.transactions = response.data.data || [];
      } catch (error) {
        console.error("Error loading transactions:", error);
      } finally {
        this.isLoadingTransactions = false;
      }
    },
    
    async loadAppointments() {
      try {
        this.isLoadingAppointments = true;
        const response = await axios.get(`/appointments?doctor_id=${this.decryptIt(this.$route.params.doctorId)}`);
        this.appointments = response.data.data || [];
      } catch (error) {
        console.error("Error loading appointments:", error);
      } finally {
        this.isLoadingAppointments = false;
      }
    },
    
    async loadAvailability() {
      try {
        this.isLoadingAvailability = true;
        const doctorId = this.decryptIt(this.$route.params.doctorId);
        const response = await axios.get(`/doctor_available_days?user_id=${doctorId}&context=panel`);
        const days = response.data.data || [];
        const order = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
        this.availabilityDays = days.slice().sort((a, b) => order.indexOf(a.day) - order.indexOf(b.day));
      } catch (error) {
        console.error('Error loading availability:', error);
        this.availabilityDays = [];
      } finally {
        this.isLoadingAvailability = false;
      }
    },

    async loadConsultations() {
      try {
        this.isLoadingConsultations = true;
        const response = await axios.get(`/consultations?doctor_id=${this.decryptIt(this.$route.params.doctorId)}`);
        this.consultations = response.data.data || [];
      } catch (error) {
        console.error("Error loading consultations:", error);
      } finally {
        this.isLoadingConsultations = false;
      }
    },

    async loadReferrals() {
      try {
        this.isLoadingReferrals = true;
        const response = await axios.get(`/users?referred_user_id=${this.decryptIt(this.$route.params.doctorId)}&context=panel`);
        this.referrals = response.data.data || [];
      } catch (error) {
        console.error("Error loading referrals:", error);
      } finally {
        this.isLoadingReferrals = false;
      }
    },


    getFullName(user) {
      if (!user) return "Unknown";
      const firstName = user.first_name || "";
      const lastName = user.last_name || "";
      return `${firstName} ${lastName}`.trim() || user.email || "No name";
    },
    
    getImageUrl(imagePath) {
      if (!imagePath) return "";
      return this.constructMediaUrl(imagePath);
    },
    
    constructMediaUrl(filePath) {
      if (!filePath) return "";
      
      // Remove leading slash from filePath if it exists
      const cleanFilePath = filePath.startsWith('/') ? filePath.substring(1) : filePath;
      
      // Ensure mediaUrl doesn't end with double slashes
      let baseUrl = this.mediaUrl;
      if (baseUrl.endsWith('//')) {
        baseUrl = baseUrl.slice(0, -1); // Remove one trailing slash
      } else if (!baseUrl.endsWith('/')) {
        baseUrl += '/';
      }
      
      return baseUrl + cleanFilePath;
    },
    
    formatCurrency(amount) {
      const value = parseFloat(amount) || 0;
      return value.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-IN');
    },
    
    
    getPathyVerificationColor(isVerified) {
      return isVerified ? 'success' : 'warning';
    },
    
    getPathyVerificationIcon(isVerified) {
      return isVerified ? 'mdi-check-circle' : 'mdi-clock';
    },
    
    getPathyVerificationText(isVerified) {
      return isVerified ? 'Verified' : 'Pending Verification';
    },
    
    getStatusColor(status) {
      // Appointment status (App\Enums\AppointmentStatus): 0=Pending, 1=Confirmed, 2=Completed,
      // 3=CancelledByUser, 4=CancelledByDoctor, 5=Rescheduled, 6=NoShow, 7=Expired
      switch (parseInt(status)) {
        case 1: return 'success';
        case 2: return 'info';
        case 0: return 'warning';
        case 3:
        case 4:
        case 6: return 'error';
        case 5: return 'primary';
        default: return 'grey';
      }
    },

    getStatusText(status) {
      switch (parseInt(status)) {
        case 0: return 'Pending';
        case 1: return 'Confirmed';
        case 2: return 'Completed';
        case 3: return 'Cancelled by User';
        case 4: return 'Cancelled by Doctor';
        case 5: return 'Rescheduled';
        case 6: return 'No Show';
        case 7: return 'Expired';
        default: return 'Unknown';
      }
    },
    
    getEducationStatusColor(status) {
      // Education status: 0=Pending, 1=Approved, 2=Rejected
      switch (parseInt(status)) {
        case 1: return 'success'; // Approved
        case 2: return 'error';   // Rejected  
        case 0: return 'warning'; // Pending
        default: return 'grey';
      }
    },
    
    getEducationStatusText(status) {
      // Education status: 0=Pending, 1=Approved, 2=Rejected
      switch (parseInt(status)) {
        case 1: return 'Verified';
        case 2: return 'Rejected';
        case 0: return 'Pending';
        default: return 'Unknown';
      }
    },
    
    getEducationStatusIcon(status) {
      switch (parseInt(status)) {
        case 1: return 'mdi-check-circle';
        case 2: return 'mdi-close-circle';
        case 0: return 'mdi-clock-outline';
        default: return 'mdi-help-circle';
      }
    },
    
    formatTime(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getConsultationStatusText(status) {
      const map = { 0: "Scheduled", 1: "In Progress", 2: "Completed", 3: "Cancelled", 4: "Missed" };
      return map[status] ?? "Unknown";
    },
    getConsultationStatusColor(status) {
      const map = { 0: "blue", 1: "orange", 2: "success", 3: "error", 4: "grey" };
      return map[status] ?? "grey";
    },
    getConsultationStatusIcon(status) {
      const map = { 0: "mdi-calendar-clock", 1: "mdi-video", 2: "mdi-check-circle", 3: "mdi-close-circle", 4: "mdi-account-off" };
      return map[status] ?? "mdi-help-circle";
    },
    getConsultationDuration(item) {
      if (!item.start_time || !item.end_time) return '-';
      const minutes = Math.round((new Date(item.end_time) - new Date(item.start_time)) / 60000);
      if (minutes < 1) return '< 1 min';
      if (minutes < 60) return `${minutes} min`;
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    },
    getConsultationPayment(item) {
      if (item.consultation_billings && item.consultation_billings.length > 0) {
        const total = item.consultation_billings[0].total_amount_deducted;
        if (total > 0) return `₹${total}`;
      }
      if (item.appointment && item.appointment.amount_paid) {
        return `₹${parseFloat(item.appointment.amount_paid).toFixed(0)}`;
      }
      return '-';
    },

    getTransactionStatusColor(status) {
      // Transaction status: 0=Pending, 1=Completed, 2=Failed
      switch (parseInt(status)) {
        case 1: return 'success';
        case 0: return 'warning';
        case 2: return 'error';
        default: return 'grey';
      }
    },

    getTransactionStatusIcon(status) {
      // Transaction status: 0=Pending, 1=Completed, 2=Failed
      switch (parseInt(status)) {
        case 1: return 'mdi-check-circle';
        case 0: return 'mdi-clock-outline';
        case 2: return 'mdi-alert-circle';
        default: return 'mdi-help-circle';
      }
    },

    getTransactionStatusText(status) {
      // Transaction status: 0=Pending, 1=Completed, 2=Failed
      switch (parseInt(status)) {
        case 1: return 'Completed';
        case 0: return 'Pending';
        case 2: return 'Failed';
        default: return 'Unknown';
      }
    },

    isCreditTransaction(type) {
      // Transaction type: 1=Debit, 2=Credit
      return parseInt(type) === 2;
    },

    getTransactionTypeText(type) {
      switch (parseInt(type)) {
        case 2: return 'Credit';
        case 1: return 'Debit';
        default: return 'Unknown';
      }
    },
    
    getAppointmentModeText(item) {
      if (item.is_videocall) return 'Video';
      if (item.is_audiocall) return 'Audio';
      return 'Chat';
    },

    getAppointmentModeColor(item) {
      if (item.is_videocall) return 'blue';
      if (item.is_audiocall) return 'green';
      return 'purple';
    },

    getAppointmentModeIcon(item) {
      if (item.is_videocall) return 'mdi-video';
      if (item.is_audiocall) return 'mdi-phone';
      return 'mdi-chat';
    },

    getStatusIcon(status) {
      switch (parseInt(status)) {
        case 1: return 'mdi-check-circle';
        case 2: return 'mdi-check-all';
        case 3:
        case 4: return 'mdi-cancel';
        case 0: return 'mdi-clock-outline';
        case 5: return 'mdi-calendar-edit';
        case 6: return 'mdi-account-off';
        case 7: return 'mdi-clock-alert';
        default: return 'mdi-calendar';
      }
    },
    
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    
    getCertificateValidityColor(expiryDate) {
      if (!expiryDate) return 'grey';
      const today = new Date();
      const expiry = new Date(expiryDate);
      const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry < 0) return 'error'; // Expired
      if (daysUntilExpiry < 30) return 'warning'; // Expires soon
      return 'success'; // Valid
    },
    
    getCertificateValidityIcon(expiryDate) {
      if (!expiryDate) return 'mdi-help-circle';
      const today = new Date();
      const expiry = new Date(expiryDate);
      const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry < 0) return 'mdi-alert-circle';
      if (daysUntilExpiry < 30) return 'mdi-clock-alert';
      return 'mdi-shield-check';
    },
    
    getCertificateValidityText(expiryDate) {
      if (!expiryDate) return 'Unknown';
      const today = new Date();
      const expiry = new Date(expiryDate);
      const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry < 0) return 'Expired';
      if (daysUntilExpiry < 30) return 'Expires Soon';
      return 'Valid';
    },
    
    async approveEducation(educationId) {
      try {
        await axios.post(`/education_details/approve/${this.encryptIt(educationId)}`);
        this.$toast.success("Education details approved successfully!");
        this.loadEducationDetails(); // Refresh the list
      } catch (error) {
        console.error("Error approving education:", error);
        this.$toast.error("Failed to approve education details");
      }
    },
    
    rejectEducation(educationId) {
      this.openRejectionDialog('education', educationId);
    },
    
    async markDoctorVerified() {
      if (this.isMarkingVerified) return;
      try {
        this.isMarkingVerified = true;
        const { data } = await axios.post(
          `/users/${this.encryptIt(this.doctor.id)}/mark_verified`
        );
        this.doctor.is_verified = true;
        if (data?.data?.registration_completed !== undefined) {
          this.doctor.registration_completed = !!data.data.registration_completed;
        } else {
          this.doctor.registration_completed = true;
        }
        this.$toast.success(data?.message || "Doctor marked as verified.");
      } catch (error) {
        const resp = error?.response?.data;
        if (error?.response?.status === 422 && Array.isArray(resp?.pending) && resp.pending.length) {
          this.verificationPendingItems = resp.pending;
          this.verificationPendingDialog = true;
        } else {
          this.$toast.error(resp?.message || "Failed to mark doctor as verified.");
        }
      } finally {
        this.isMarkingVerified = false;
      }
    },

    async loadPathyTypes() {
      try {
        this.isLoadingPathyTypes = true;
        const response = await axios.get("/masters", { params: { context: "user" } });
        this.pathyTypes = response.data?.pathy_types || response.data?.data?.pathy_types || [];
      } catch (error) {
        console.error("Error loading pathy types:", error);
      } finally {
        this.isLoadingPathyTypes = false;
      }
    },

    async updateDoctorPathy() {
      if (!this.selectedPathyId) return;
      try {
        this.isUpdatingPathy = true;
        const response = await axios.patch(
          `/users/${this.encryptIt(this.doctor.id)}/pathy`,
          { pathy_id: this.selectedPathyId }
        );
        const updated = response.data?.data?.pathy;
        if (updated) {
          this.$set(this.doctor, "pathy", updated);
        }
        this.selectedPathyId = null;
        this.editingPathy = false;
        this.pathyRejected = false;
        this.$toast.success("Pathy type updated successfully!");
      } catch (error) {
        console.error("Error updating pathy type:", error);
        this.$toast.error(
          error.response?.data?.message || "Failed to update pathy type"
        );
      } finally {
        this.isUpdatingPathy = false;
      }
    },

    async approvePathyIntegration() {
      try {
        this.isApprovingPathy = true;
        await axios.patch(`/users/${this.encryptIt(this.doctor.id)}/pathy_verification`, {
          is_verified_pathy: true
        });
        this.$toast.success("Pathy integration approved successfully!");
        this.doctor.is_verified_pathy = true; // Update local state
      } catch (error) {
        console.error("Error approving pathy integration:", error);
        this.$toast.error("Failed to approve pathy integration");
      } finally {
        this.isApprovingPathy = false;
      }
    },
    
    async rejectPathyIntegration() {
      try {
        this.isRejectingPathy = true;
        await axios.patch(`/users/${this.encryptIt(this.doctor.id)}/pathy_verification`, {
          is_verified_pathy: false
        });
        this.$toast.success("Pathy integration rejected successfully!");
        this.doctor.is_verified_pathy = false;
        this.pathyRejected = true;
      } catch (error) {
        console.error("Error rejecting pathy integration:", error);
        this.$toast.error("Failed to reject pathy integration");
      } finally {
        this.isRejectingPathy = false;
      }
    },
    
    async toggleConsultation() {
      try {
        this.isUpdatingConsultation = true;
        // v-model already updated doctor.is_consultation — send the new value directly
        const newVal = this.doctor.is_consultation ? 1 : 0;
        await axios.patch(`/users/${this.$route.params.doctorId}`, { is_consultation: newVal });
        this.$toast.success(`Consultation ${newVal ? 'enabled' : 'disabled'} successfully`);
      } catch (error) {
        this.doctor.is_consultation = !this.doctor.is_consultation; // revert on error
        this.$toast.error("Failed to update consultation status");
      } finally {
        this.isUpdatingConsultation = false;
      }
    },

    viewIdDocument(path) {
      window.open(this.constructMediaUrl(path), '_blank');
    },

    async uploadIdDocument(field, fileRef, loadingRef) {
      try {
        this[loadingRef] = true;
        const fd = new FormData();
        fd.append(field, this[fileRef]);
        const { data } = await axios.patch(`/users/${this.$route.params.doctorId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const column = field + '_path';
        this.$set(this.doctor, column, data.data?.[column] || this.doctor[column]);
        this[fileRef] = null;
        this.$toast.success("Document uploaded successfully");
      } catch (error) {
        this.$toast.error("Failed to upload document");
      } finally {
        this[loadingRef] = false;
      }
    },

    async deleteIdDocument(column) {
      const loadingRef = column === 'professional_id_path' ? 'isDeletingProfId' : 'isDeletingGovId';
      try {
        this[loadingRef] = true;
        await axios.patch(`/users/${this.$route.params.doctorId}`, { [column]: null });
        this.$set(this.doctor, column, null);
        this.$toast.success("Document removed successfully");
      } catch (error) {
        this.$toast.error("Failed to remove document");
      } finally {
        this[loadingRef] = false;
      }
    },

    async approveCertificate(certificate) {
      try {
        this.isLoadingCertificates = true;
        await axios.post(`/council_registration_certificates/approve/${this.encryptIt(certificate.id)}`);
        
        // Update the local data
        const index = this.certificates.findIndex(cert => cert.id === certificate.id);
        if (index !== -1) {
          this.$set(this.certificates[index], 'status', 1); // 1 = Approved
        }
        
        this.$toast.success('Certificate approved successfully');
      } catch (error) {
        console.error('Error approving certificate:', error);
        this.$toast.error('Failed to approve certificate');
      } finally {
        this.isLoadingCertificates = false;
      }
    },

    rejectCertificate(certificate) {
      this.openRejectionDialog('certificate', certificate);
    },

    async approveBankDetails(bankDetail) {
      try {
        this.isLoadingBank = true;
        await axios.post(`/bank_details/${this.encryptIt(bankDetail.id)}/approve`);
        
        // Update the local data
        const index = this.bankDetails.findIndex(bank => bank.id === bankDetail.id);
        if (index !== -1) {
          this.$set(this.bankDetails[index], 'status', 1); // 1 = Approved
        }
        
        this.$toast.success('Bank details approved successfully');
      } catch (error) {
        console.error('Error approving bank details:', error);
        this.$toast.error('Failed to approve bank details');
      } finally {
        this.isLoadingBank = false;
      }
    },

    rejectBankDetails(bankDetail) {
      this.openRejectionDialog('bankDetails', bankDetail);
    },

    openEditEducation(item) {
      this.editEducationId = item.id;
      this.editEducationCurrentCert = item.certificate_file_path || null;
      this.editEducationForm = {
        qualification: item.qualification || "",
        university: item.university || "",
        specialization: item.specialization || "",
        year_of_passing: item.year_of_passing || "",
        is_active: item.is_active !== undefined ? item.is_active : true,
        certificate_file: null,
      };
      this.editEducationErrors = {};
      this.editEducationDialog = true;
    },
    async saveEditEducation() {
      this.isSavingEducation = true;
      this.editEducationErrors = {};
      try {
        const url = `/education_details/${this.encryptIt(this.editEducationId)}`;
        if (this.editEducationForm.certificate_file) {
          const fd = new FormData();
          fd.append('_method', 'PATCH');
          fd.append('qualification', this.editEducationForm.qualification);
          fd.append('university', this.editEducationForm.university);
          fd.append('specialization', this.editEducationForm.specialization || '');
          fd.append('year_of_passing', this.editEducationForm.year_of_passing);
          fd.append('is_active', this.editEducationForm.is_active ? '1' : '0');
          fd.append('certificate_file', this.editEducationForm.certificate_file);
          await axios.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          // eslint-disable-next-line no-unused-vars
          const { certificate_file, ...payload } = this.editEducationForm;
          await axios.patch(url, payload);
        }
        this.editEducationDialog = false;
        await this.loadEducationDetails();
      } catch (err) {
        if (err.response?.status === 422) this.editEducationErrors = err.response.data.errors || {};
      } finally {
        this.isSavingEducation = false;
      }
    },

    openEditBank(item) {
      this.editBankId = item.id;
      this.editBankForm = {
        account_name: item.account_name || "",
        bank_name: item.bank_name || "",
        account_number: item.account_number || "",
        ifsc_code: item.ifsc_code || "",
        branch: item.branch || "",
        micr_code: item.micr_code || "",
      };
      this.editBankErrors = {};
      this.editBankDialog = true;
    },
    async saveEditBank() {
      this.isSavingBank = true;
      this.editBankErrors = {};
      try {
        await axios.patch(`/bank_details/${this.encryptIt(this.editBankId)}`, this.editBankForm);
        this.editBankDialog = false;
        await this.loadBankDetails();
      } catch (err) {
        if (err.response?.status === 422) this.editBankErrors = err.response.data.errors || {};
      } finally {
        this.isSavingBank = false;
      }
    },

    openEditCertificate(item) {
      this.editCertificateId = item.id;
      this.editCertificateCurrentFile = item.certificate_path || null;
      this.editCertificateForm = {
        council_name: item.council_name || "",
        registration_number: item.registration_number || "",
        registration_year: item.registration_year || "",
        expiry_date: item.expiry_date ? item.expiry_date.substring(0, 10) : "",
        renewal_date: item.renewal_date ? item.renewal_date.substring(0, 10) : "",
        certificate_path: null,
      };
      this.editCertificateErrors = {};
      this.editCertificateDialog = true;
    },
    async saveEditCertificate() {
      this.isSavingCertificate = true;
      this.editCertificateErrors = {};
      try {
        const url = `/council_registration_certificates/${this.encryptIt(this.editCertificateId)}`;
        if (this.editCertificateForm.certificate_path) {
          const fd = new FormData();
          fd.append('_method', 'PATCH');
          fd.append('council_name', this.editCertificateForm.council_name);
          fd.append('registration_number', this.editCertificateForm.registration_number);
          fd.append('registration_year', this.editCertificateForm.registration_year || '');
          fd.append('expiry_date', this.editCertificateForm.expiry_date || '');
          fd.append('renewal_date', this.editCertificateForm.renewal_date || '');
          fd.append('certificate_path', this.editCertificateForm.certificate_path);
          await axios.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          // eslint-disable-next-line no-unused-vars
          const { certificate_path, ...payload } = this.editCertificateForm;
          await axios.patch(url, payload);
        }
        this.editCertificateDialog = false;
        await this.loadCertificates();
      } catch (err) {
        if (err.response?.status === 422) this.editCertificateErrors = err.response.data.errors || {};
      } finally {
        this.isSavingCertificate = false;
      }
    },

    async approveDocument(document) {
      try {
        this.isLoadingDocuments = true;
        await axios.patch(`/user_documents/${this.encryptIt(document.id)}`, {
          status: 1 // 1 = Approved
        });
        
        // Update the local data
        const index = this.documents.findIndex(doc => doc.id === document.id);
        if (index !== -1) {
          this.$set(this.documents[index], 'status', 1);
          this.$set(this.documents[index], 'verified_by_id', this.$store.getters.user?.id);
          this.$set(this.documents[index], 'verified_at', new Date().toISOString());
        }
        
        this.$toast.success('Document approved successfully');
      } catch (error) {
        console.error('Error approving document:', error);
        this.$toast.error('Failed to approve document');
      } finally {
        this.isLoadingDocuments = false;
      }
    },

    rejectDocument(document) {
      this.openRejectionDialog('document', document);
    },

    // Rejection Dialog Methods
    openRejectionDialog(type, item) {
      this.rejectionType = type;
      this.rejectionItem = item;
      this.rejectionReason = "";
      this.rejectionReasonError = "";
      this.showRejectionDialog = true;
    },

    closeRejectionDialog() {
      this.showRejectionDialog = false;
      this.rejectionReason = "";
      this.rejectionReasonError = "";
      this.rejectionItem = null;
      this.rejectionType = null;
    },

    async confirmRejection() {
      // Validate reason
      if (!this.rejectionReason || this.rejectionReason.length < 10) {
        this.rejectionReasonError = "Please provide a detailed rejection reason (at least 10 characters)";
        return;
      }

      this.isSubmittingRejection = true;

      try {
        switch (this.rejectionType) {
          case 'education':
            await this.submitEducationRejection();
            break;
          case 'certificate':
            await this.submitCertificateRejection();
            break;
          case 'bankDetails':
            await this.submitBankDetailsRejection();
            break;
          case 'document':
            await this.submitDocumentRejection();
            break;
        }
        this.closeRejectionDialog();
      } catch (error) {
        console.error('Error rejecting item:', error);
        this.$toast.error(`Failed to reject ${this.rejectionTypeLabel.toLowerCase()}`);
      } finally {
        this.isSubmittingRejection = false;
      }
    },

    async submitEducationRejection() {
      await axios.post(`/education_details/reject/${this.encryptIt(this.rejectionItem)}`, {
        rejection_reason: this.rejectionReason
      });
      this.$toast.success("Education details rejected successfully!");
      this.loadEducationDetails();
    },

    async submitCertificateRejection() {
      await axios.post(`/council_registration_certificates/reject/${this.encryptIt(this.rejectionItem.id)}`, {
        rejection_reason: this.rejectionReason
      });

      // Update the local data
      const index = this.certificates.findIndex(cert => cert.id === this.rejectionItem.id);
      if (index !== -1) {
        this.$set(this.certificates[index], 'status', 2);
        this.$set(this.certificates[index], 'rejection_reason', this.rejectionReason);
      }

      this.$toast.success('Certificate rejected successfully');
    },

    async submitBankDetailsRejection() {
      await axios.post(`/bank_details/${this.encryptIt(this.rejectionItem.id)}/reject`, {
        rejection_reason: this.rejectionReason
      });

      // Update the local data
      const index = this.bankDetails.findIndex(bank => bank.id === this.rejectionItem.id);
      if (index !== -1) {
        this.$set(this.bankDetails[index], 'status', 2);
        this.$set(this.bankDetails[index], 'rejection_reason', this.rejectionReason);
      }

      this.$toast.success('Bank details rejected successfully');
    },

    async submitDocumentRejection() {
      await axios.patch(`/user_documents/${this.encryptIt(this.rejectionItem.id)}`, {
        status: 2,
        rejection_reason: this.rejectionReason
      });

      // Update the local data
      const index = this.documents.findIndex(doc => doc.id === this.rejectionItem.id);
      if (index !== -1) {
        this.$set(this.documents[index], 'status', 2);
        this.$set(this.documents[index], 'rejection_reason', this.rejectionReason);
      }

      this.$toast.success('Document rejected successfully');
    },

    viewDocument(documentPath) {
      if (!documentPath) return;
      this.docViewerUrl = this.constructMediaUrl(documentPath);
      this.docViewerDialog = true;
    },

    getFileIcon(filePath) {
      if (!filePath) return 'mdi-file';
      
      const extension = filePath.split('.').pop()?.toLowerCase();
      switch (extension) {
        case 'pdf':
          return 'mdi-file-pdf-box';
        case 'doc':
        case 'docx':
          return 'mdi-file-word-box';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          return 'mdi-file-image';
        default:
          return 'mdi-file-document';
      }
    },

    getFileName(filePath) {
      if (!filePath) return '';
      return filePath.split('/').pop();
    },



    viewCertificate(certificatePath) {
      if (!certificatePath) return;
      this.docViewerUrl = this.constructMediaUrl(certificatePath);
      this.docViewerDialog = true;
    },
  },
};
</script>

<style scoped>
.pointer {
  cursor: pointer;
}

/* Clean Header Card */
.header-card {
  background: #ffffff;
  border-radius: 16px !important;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
  overflow: hidden;
}

.header-banner {
  min-height: 124px;
  padding: 18px 24px 0;
  background:
    radial-gradient(at 80% 20%, rgba(76, 175, 80, 0.18), transparent 60%),
    radial-gradient(at 20% 80%, rgba(33, 150, 243, 0.18), transparent 60%),
    linear-gradient(135deg, #f5f7fa 0%, #eef2f7 100%);
  border-bottom: 1px solid #ecf0f4;
}
.header-card-body {
  margin-top: -48px;
  position: relative;
}
.avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.doctor-avatar {
  border: 4px solid #ffffff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  background: #f5f7fa;
  transition: all 0.3s ease;
}

.doctor-avatar--clickable {
  cursor: zoom-in;
}
.avatar-preview-card {
  position: relative;
  overflow: hidden;
  background: #0f1720;
}
.avatar-preview-img {
  display: block;
  width: auto;
  height: calc(100vh - 150px); /* fill the screen height; width follows the photo's shape */
  max-width: 96vw;
  object-fit: contain;
  margin: 0 auto;
}
.avatar-preview-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  background: rgba(255, 255, 255, 0.92) !important;
}
.avatar-preview-caption {
  padding: 10px 16px;
  text-align: center;
  font-weight: 600;
  color: #fff;
  background: #0f1720;
}
.doctor-avatar:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16);
}

.status-indicator {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: white;
  border-radius: 50%;
  padding: 3px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
}

.status-dot {
  display: block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #bdbdbd;
}
.status-dot.is-online {
  background: #4caf50;
  box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.18);
}
.status-dot.is-offline {
  background: #bdbdbd;
}

.role-pill {
  display: inline-flex;
  align-items: center;
  background: rgba(25, 118, 210, 0.08);
  color: #1976d2;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.contact-pill {
  display: inline-flex;
  align-items: center;
  background: #f5f7fa;
  color: rgba(0, 0, 0, 0.72);
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12.5px;
  margin-right: 8px;
  margin-bottom: 4px;
  text-decoration: none;
  border: 1px solid #ecf0f4;
  transition: all 0.2s ease;
}
.contact-pill:hover {
  background: #eef2f7;
  color: #1976d2;
  border-color: #d6e2ee;
}

/* Clean Typography */
.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.3;
  color: #2c3e50;
}

.page-subtitle {
  font-size: 1rem;
  line-height: 1.4;
  font-weight: 400;
}

/* Clean Chips */
.speciality-chip {
  transition: all 0.3s ease;
  border-width: 1px !important;
}

.speciality-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

.status-chip,
.approval-chip,
.wallet-chip {
  transition: all 0.3s ease;
  font-weight: 500;
}

.status-chip:hover,
.approval-chip:hover,
.wallet-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

/* Clean Action Buttons */
.action-btn {
  text-transform: none !important;
  font-weight: 500;
  transition: all 0.3s ease;
  min-width: 100px;
  height: 40px;
  margin-left: 8px;
  margin-bottom: 6px;
}

.action-btn:first-child {
  margin-left: 0;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Action Button Containers */
.action-button-container {
  gap: 0;
}

.approval-actions {
  display: flex;
  gap: 8px;
  margin-right: 8px;
}

.approval-actions .action-btn {
  margin-left: 0;
}

/* Table Action Button Spacing */
.v-data-table .action-buttons .v-btn {
  margin: 0 3px;
}

.v-data-table .action-buttons .v-btn:first-child {
  margin-left: 0;
}

.v-data-table .action-buttons .v-btn:last-child {
  margin-right: 0;
}

/* Override mr-1, mr-2 classes in action contexts */
.action-buttons .v-btn.mr-1,
.action-buttons .v-btn.mr-2 {
  margin-right: 6px !important;
}

.action-buttons .v-btn.mr-1:last-child,
.action-buttons .v-btn.mr-2:last-child {
  margin-right: 0 !important;
}

/* Modern Tabs */
.tabs-card {
  border-radius: 16px !important;
  background: #ffffff;
  border: 1px solid #e0e0e0;
}

.tabs-container {
  border-radius: 16px 16px 0 0;
}

.tab-item {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.25px;
  transition: all 0.3s ease;
  min-height: 56px;
  padding: 0 16px;
}

.tab-item:hover {
  background: rgba(25, 118, 210, 0.04) !important;
}

.tab-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-icon {
  margin-right: 0 !important;
}

.tab-text {
  font-size: 0.875rem;
  font-weight: 500;
}

.tab-badge {
  font-size: 0.7rem !important;
  height: 18px !important;
  min-width: 18px !important;
}

/* Clean Profile Content */
.profile-content {
  background: #f8f9fa;
}

.info-card {
  background: #ffffff;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
}

.info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08) !important;
  border-color: #1976d2;
}

.card-header {
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
  margin: 0;
}

.card-title-text {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
}

/* Clean Info Items */
.info-item {
  margin-bottom: 0.75rem;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.info-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 12px;
}

.info-item:hover {
  background: #f8f9fa;
  border-radius: 8px;
  border-bottom: 1px solid transparent;
}

.info-item:hover:not(:last-child) {
  margin-bottom: 0.75rem;
}

.info-item strong {
  color: #2c3e50;
  font-weight: 500;
  min-width: 130px;
  font-size: 0.875rem;
}

/* New cleaner key/value rows */
.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.18s ease;
}
.info-row:last-child { border-bottom: none; }
.info-row:hover { background: #f8fafc; }

.info-row-stacked {
  flex-direction: column;
  align-items: flex-start;
}
.info-row-stacked .info-value {
  text-align: left;
  margin-top: 4px;
  white-space: pre-line;
}

.info-label {
  display: inline-flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.55);
  font-size: 12.5px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  flex-shrink: 0;
}
.info-label .v-icon {
  color: rgba(0, 0, 0, 0.42);
}

.info-value {
  color: #2c3e50;
  font-size: 14px;
  text-align: right;
  word-break: break-word;
}
.info-value.is-muted {
  color: rgba(0, 0, 0, 0.38);
  font-style: italic;
}

.online-bullet {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #bdbdbd;
  margin-right: 6px;
}
.online-bullet.is-on {
  background: #4caf50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.18);
}

/* Modern Data Tables */
.modern-table.v-data-table >>> .v-data-table__wrapper {
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
}

.modern-table.v-data-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 2px solid #e0e0e0;
}

.modern-table.v-data-table >>> .v-data-table-header th {
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  color: #2c3e50 !important;
  letter-spacing: 0.5px !important;
  padding: 16px 12px !important;
  white-space: nowrap !important;
}

.modern-table.v-data-table >>> .v-data-table-header th span {
  display: inline-flex;
  align-items: center;
}

.modern-table.v-data-table >>> tbody tr {
  transition: all 0.3s ease;
  border-bottom: 1px solid #f0f0f0;
}

.modern-table.v-data-table >>> tbody tr:hover {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%) !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.modern-table.v-data-table >>> tbody tr td {
  padding: 16px 12px !important;
  vertical-align: top !important;
}

/* Table Cell Styles */
.qualification-cell,
.university-cell,
.account-cell,
.bank-cell,
.patient-cell,
.datetime-cell,
.diagnosis-cell,
.transaction-id-cell,
.date-cell {
  min-height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.cell-vcenter {
  min-height: 44px;
  display: flex;
  align-items: center;
}

.year-chip,
.status-chip {
  transition: all 0.2s ease;
  font-weight: 500;
}

.year-chip:hover,
.status-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.action-buttons {
  gap: 6px;
  min-height: 40px;
}

.action-btn-small {
  transition: all 0.2s ease;
  min-width: 32px !important;
  height: 32px !important;
  margin: 0 2px;
}

.action-btn-small:first-child {
  margin-left: 0;
}

.action-btn-small:last-child {
  margin-right: 0;
}

.action-btn-small:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

/* Modern Chips in Tables */
.modern-table .v-chip {
  font-weight: 500;
  letter-spacing: 0.25px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.75rem;
  border-radius: 8px;
}

.modern-table .v-chip:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modern-table .v-chip.v-chip--outlined {
  border-width: 1.5px;
}

/* Enhanced Table Footer */
.modern-table.v-data-table >>> .v-data-footer {
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
  padding: 8px 16px;
}

.modern-table.v-data-table >>> .v-data-footer .v-select {
  margin: 0;
}

/* Loading State */
.modern-table.v-data-table >>> .v-data-table__loading {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(2px);
}

/* Empty State Styling */
.modern-table .v-data-table__empty-wrapper {
  padding: 60px 20px;
}

.modern-table .v-data-table__empty-wrapper .v-icon {
  opacity: 0.3;
}

/* Pagination Styling */
.modern-table.v-data-table >>> .v-pagination {
  justify-content: center;
}

.modern-table.v-data-table >>> .v-pagination .v-pagination__item {
  border-radius: 8px;
  transition: all 0.2s ease;
}

.modern-table.v-data-table >>> .v-pagination .v-pagination__item:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Loading States */
.v-skeleton-loader {
  border-radius: 8px !important;
}

/* Enhanced Dialogs */
.v-dialog >>> .v-card {
  border-radius: 16px !important;
}

/* Responsive Table Design */
@media (max-width: 1264px) {
  .page-title {
    font-size: 1.5rem;
  }
  
  .doctor-avatar {
    width: 80px !important;
    height: 80px !important;
  }
  
  .modern-table.v-data-table >>> tbody tr td {
    padding: 12px 8px !important;
  }
  
  .qualification-cell,
  .university-cell,
  .patient-cell {
    min-height: 50px;
  }
}

@media (max-width: 960px) {
  .page-title {
    font-size: 1.3rem;
  }
  
  .page-subtitle {
    font-size: 0.9rem;
  }
  
  .doctor-avatar {
    width: 75px !important;
    height: 75px !important;
  }
  
  .action-btn {
    min-width: 85px;
    font-size: 0.875rem;
  }
  
  .avatar-wrapper {
    margin-right: 1rem !important;
  }
}

@media (max-width: 600px) {
  .header-card .pa-6 {
    padding: 1rem !important;
  }
  
  .page-title {
    font-size: 1.2rem;
  }
  
  .doctor-avatar {
    width: 65px !important;
    height: 65px !important;
  }
  
  .tab-text {
    display: none;
  }
  
  .tab-badge {
    margin-left: 0 !important;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    padding: 8px 16px;
  }
  
  .info-item strong {
    min-width: auto;
    margin-bottom: 4px;
  }
  
  .profile-content {
    padding: 1rem !important;
  }
  
  .action-btn {
    min-width: 80px;
    font-size: 0.8rem;
  }
}

/* Animation Classes */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.info-card {
  animation: fadeInUp 0.6s ease-out;
}

.info-card:nth-child(1) { animation-delay: 0.1s; }
.info-card:nth-child(2) { animation-delay: 0.2s; }
.info-card:nth-child(3) { animation-delay: 0.3s; }
.info-card:nth-child(4) { animation-delay: 0.4s; }
.info-card:nth-child(5) { animation-delay: 0.5s; }
.info-card:nth-child(6) { animation-delay: 0.6s; }

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Print Styles */
@media print {
  .action-btn,
  .v-tabs,
  .header-background {
    display: none !important;
  }
  
  .header-card {
    background: #ffffff !important;
    color: #000000 !important;
  }
  
  .page-title,
  .page-subtitle,
  .contact-info {
    color: #000000 !important;
  }
}

/* Dark Theme Support */
.theme--dark .info-card {
  background: #424242 !important;
  border-color: #616161;
}

.theme--dark .card-header {
  background: linear-gradient(135deg, #515151 0%, #424242 100%);
  border-bottom-color: #616161;
}

.theme--dark .info-item:hover {
  background: rgba(255, 255, 255, 0.04);
}

/* Cursor pointer utility */
.cursor-pointer {
  cursor: pointer;
}

/* ── Availability Tab ─────────────────────────── */
.avail-loading,
.avail-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 16px;
}

.avail-grid {
  margin-top: 4px;
}

.avail-day-card {
  border-radius: 10px;
  padding: 14px 16px;
  height: 100%;
  border: 1.5px solid #e8eaf0;
  background: #ffffff;
  transition: box-shadow 0.15s ease;
}
.avail-day-card:hover {
  box-shadow: 0 3px 12px rgba(0,0,0,0.08);
}
.avail-day-card--active {
  border-color: #c8e6c9;
  background: linear-gradient(135deg, #ffffff 0%, #f9fffe 100%);
}
.avail-day-card--inactive {
  border-color: #ececec;
  background: #fafafa;
  opacity: 0.75;
}

.avail-day-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.avail-day-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
}
.avail-day-dot--green { background: #4caf50; }
.avail-day-dot--grey  { background: #bdbdbd; }

.avail-day-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2d2d2d;
  letter-spacing: 0.01em;
}

.avail-slot-summary {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}

.avail-slots-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 4px;
}

.avail-slot-chip {
  display: inline-flex;
  align-items: center;
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 0.68rem;
  font-weight: 500;
  white-space: nowrap;
  letter-spacing: 0.01em;
}

.avail-no-slots {
  display: flex;
  align-items: center;
  color: #9e9e9e;
  font-size: 0.75rem;
  margin-top: 6px;
}

.avail-inactive-msg {
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.prof-field >>> .v-input__control {
  border-radius: 8px !important;
}

/* ── ID Documents & Consultation card ── */
.iddoc-consult-row {
  background: linear-gradient(135deg, #f0f7ff 0%, #fafeff 100%);
  border-bottom: 1px solid #e8f0fe;
}

.iddoc-consult-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2c3e50;
}

.iddoc-consult-sub {
  font-size: 0.76rem;
  color: #90a4ae;
  margin-top: 2px;
}

.iddoc-doc-col {
  min-height: 200px;
}

.iddoc-doc-header {
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.iddoc-doc-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2c3e50;
}

.iddoc-file-row {
  background: #f5f9ff;
  border: 1px solid #dce8fb;
}

.iddoc-file-name {
  font-size: 0.8rem;
  font-weight: 500;
  color: #37474f;
}

.iddoc-file-sub {
  font-size: 0.72rem;
  color: #90a4ae;
}

.iddoc-empty-zone {
  background: #fafafa;
  border: 1.5px dashed #e0e0e0;
  min-height: 80px;
}

.theme--dark .iddoc-consult-row {
  background: linear-gradient(135deg, #1a2540 0%, #1e2a3a 100%);
  border-bottom-color: #2a3a50;
}

.theme--dark .iddoc-file-row {
  background: #1e2a3a;
  border-color: #2a3a50;
}

.theme--dark .iddoc-empty-zone {
  background: #1a1f2e;
  border-color: #2a3550;
}
</style>